import {
  ArrowLeftRight,
  BadgeCheck,
  Briefcase,
  Building2,
  CalendarClock,
  CircleHelp,
  ClipboardList,
  ExternalLink,
  FileBarChart,
  HandCoins,
  Headphones,
  Home,
  ListChecks,
  MessageSquare,
  Package,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePermissions } from "../../rbac/PermissionsContext";
import { TOP_NAV_MODULE_KEYS } from "../../rbac/catalog";
import { useMyTeamNavVisibility } from "../../hooks/useMyTeamNavVisibility";
import { getMaxDateInputValue } from "../../lib/attendanceDayChecks";
import {
  employeeStubFromIds,
  resolveEmployeeAttendanceRequestAccess,
} from "../../lib/attendanceRequestAccess";
import {
  allowEssQuickPanelScreen,
  canRaiseEssRequestType,
} from "../../lib/essQuickPanelRequestAccess";
import {
  enrichPendingDeskRequestsWithRmNames,
  loadQuickActionRequests,
  submitAttendanceActionRequest,
  submitChangeShiftRequest,
  submitChangeWoRequest,
  submitRegulariseRequest,
  submitShortLeaveRequest,
} from "../../lib/attendanceDeskRequests";
import { cacheWeeklyOffStructureData } from "../../lib/weeklyOffChange";
import { isCompanyModuleEnabled } from "../../utils/companyModules";
import {
  canActOnRmRoutedApproval,
  isRmRoutedActionLabel,
} from "../../lib/deskApprovalRouting";
import RequestStatusDeskModal, { ALL_DESK_TYPES } from "./attendance/components/RequestStatusDeskModal";
import QuickRequestFormModal from "./attendance/components/QuickRequestFormModal";
import ResignationRequestFormModal from "./attendance/components/ResignationRequestFormModal";
import FlexiReimbursementRequestModal from "./attendance/components/FlexiReimbursementRequestModal";
import StructureReimbSyncNotifier from "./StructureReimbSyncNotifier";
import {
  buildFlexiReimbursementDeskItems,
  flexiClaimIdFromDeskId,
  flexiReimbursementDeskMode,
  isFlexiClaimDeskRequest,
  isPayrollReimbursementDeskLabel,
  PAYROLL_REIMB_CLAIM_LABEL,
  PAYROLL_REIMB_OPTIN_LABEL,
} from "../../utils/flexiReimbursementDesk";
import ShellFlipTile from "./shell/ShellFlipTile";
import AdminSecondaryNav from "./shell/AdminSecondaryNav";
import { shellFlipStore } from "./shell/shellFlipStore";
import { attendanceStructureService, employeeOnboardingService, employeeConfirmationService, employeeTransferService, employeeSeparationService, helpdeskService, employeeService, flexiBenefitsService } from "../../services/api";
import { mapLeaveRequestApiToInsight } from "../../lib/leaveApprovalWorkflow";
import {
  isOnboardingDeskRequest,
  mapOnboardingChecklistToDeskItem,
  mapOnboardingMyRequestToDeskItem,
  mapOnboardingRequestToDeskItem,
  collapseDuplicateOnboardingChecklistRows,
  ONBOARDING_REQUEST_LABEL,
} from "../../utils/onboardingApprovalDesk";
import {
  mapLifecycleDeskApiRows,
  isLifecycleDeskRequest,
  CONFIRMATION_REQUEST_LABEL,
  TRANSFER_REQUEST_LABEL,
  RESIGNATION_REQUEST_LABEL,
} from "../../utils/lifecycleApprovalDesk";
import {
  buildHelpdeskDeskItems,
  helpdeskTicketIdFromRequestId,
  TICKET_ACTION_LABEL,
} from "../../utils/helpdeskTicketDesk";
import {
  buildLeaveDeskItems,
  isLeaveActionLabel,
  isLeaveDeskRequest,
  leaveRequestIdFromDeskId,
  matchesDeskSelectedType,
} from "../../utils/leaveRequestDesk";
import {
  CANCELLATION_ACTION_LABELS,
  isCancellableActionLabel,
  isDeskCancellationRequest,
} from "../../utils/deskRequestCancellation";
import { applyOnboardingNotifications } from "../../utils/onboardingNotifications";
import {
  acknowledgeOnboardingNotify,
  isOnboardingNotifyAcknowledged,
  syncNotifyOnlyNavbarFromDesk,
} from "../../utils/onboardingNotifyAck";
import { toast } from "react-hot-toast";
import { getAuthItem, getAuthJson } from "../../utils/authStorage";
import { socket } from "../../services/websocket";
import { DsButton, DsIcon } from "../../design-system";
import {
  ADMIN_SHELL_NAV_EVENT,
  consumeActivityConsoleReturn,
  rememberActivityConsoleReturn,
} from "../../utils/adminShellNav";
import {
  groupDeskTypesByModule,
  sortDeskTypesByBadgeDesc,
} from "../../utils/deskTypeCatalog";
import {
  deskRequestIdsEqual,
  ensureUniqueDeskRequests,
  findDeskRequest,
} from "../../utils/deskRowIdentity";
import { sortDeskRequestsLatestFirst } from "../../utils/deskListOrder";
import {
  MODULE_CORE_CHILDREN,
  WORKSPACE_DEFS,
  getFeaturesForWorkspace,
} from "../../nav/workspaceRegistry";

const LEFT_SIDEBAR_WIDTH = 61;
const SECONDARY_NAV_HEIGHT = 52;

const DESK_ROUTES = {
  review: "/admin/requests",
  approvals: "/admin/approvals",
};

const DESK_STATUS_TABS = ["Pending", "Approved", "Rejected", "Undo"];
const resolveDeskStatusTab = (status) => {
  if (status === "Cancelled") return "Approved";
  if (DESK_STATUS_TABS.includes(status)) return status;
  return "Pending";
};

const REQUEST_TYPE_DEFS = [
  { key: "regularization_checks", label: "Regularise checks" },
  { key: "leave_request", label: "Leave" },
  { key: "change_shift", label: "Change Shift" },
  { key: "change_wo", label: "Change WO" },
  { key: "short_leave", label: "Short Leave" },
  { key: "od", label: "On Duty" },
  { key: "comp_off", label: "Comp Off" },
  { key: "ot", label: "Overtime" },
  { key: "restricted_holiday", label: "Restricted Holiday" },
  { key: "resignation", label: RESIGNATION_REQUEST_LABEL },
  { key: "tax_declaration", label: "Tax Planning" },
  { key: "reimbursement_opt_in", label: PAYROLL_REIMB_OPTIN_LABEL },
  { key: "reimbursement_claim", label: PAYROLL_REIMB_CLAIM_LABEL },
  { key: "ticket_request", label: "Ticket" },
  { key: "mrf_request", label: "MRF" },
];

/** My-request lifecycle types (raised from Employee module; tracked in Request desk). */
const LIFECYCLE_REQUEST_TYPE_DEFS = [
  { key: "employee_onboarding", label: ONBOARDING_REQUEST_LABEL },
  { key: "confirmation_request", label: CONFIRMATION_REQUEST_LABEL },
  { key: "transfer_request", label: TRANSFER_REQUEST_LABEL },
];

const APPROVAL_ONLY_TYPE_DEFS = [
  { key: "onboarding_approval", label: "Employee Onboarding Approval" },
  { key: "onboarding_task", label: "Onboarding Task" },
  { key: "confirmation_approval", label: "Confirmation Approval" },
  { key: "transfer_approval", label: "Transfer Approval" },
  { key: "resignation_approval", label: "Resignation Approval" },
  { key: "termination_approval", label: "Termination / Separation Approval" },
];

// `module`     -> legacy company-plan flag (module_employee, module_attendance, ...)
// `rbacModule` -> RBAC module key from src/rbac/catalog.js (controls who sees the tab)
// `children`   -> secondary aside items promoted into this left rail (accordion)
const navItems = [
  {
    name: "Configuration",
    tileLabel: "Config",
    to: "/admin/configuration",
    icon: Building2,
    module: "configuration",
    rbacModule: "configuration",
    children: MODULE_CORE_CHILDREN.configuration,
  },
  {
    name: "Employee",
    tileLabel: "Employee",
    to: "/admin/employee",
    icon: Users,
    module: "employee",
    rbacModule: "employee",
    children: MODULE_CORE_CHILDREN.employee,
  },
  {
    name: "Attendance",
    tileLabel: "Attendance",
    to: "/admin/attendance",
    icon: CalendarClock,
    module: "attendance",
    rbacModule: "attendance",
    children: MODULE_CORE_CHILDREN.attendance,
  },
  {
    name: "Payroll",
    tileLabel: "Payroll",
    to: "/admin/payroll",
    icon: HandCoins,
    module: "payroll",
    rbacModule: "payroll",
    children: MODULE_CORE_CHILDREN.payroll,
  },
  {
    name: "Hiring",
    tileLabel: "Hiring",
    to: "/admin/hiring",
    icon: Briefcase,
    module: "hiring",
    rbacModule: "hiring",
    children: MODULE_CORE_CHILDREN.hiring,
  },
  {
    name: "Assets",
    tileLabel: "Assets",
    to: "/admin/assets",
    icon: Package,
    module: "assets",
    rbacModule: "assets",
    children: MODULE_CORE_CHILDREN.assets,
  },
  {
    name: "Communication",
    tileLabel: "Comms",
    to: "/admin/communication",
    icon: MessageSquare,
    module: "communication",
    rbacModule: "communication",
    children: [
      { key: "flash_news", label: "Flash news" },
      { key: "announcement", label: "Announcements" },
      { key: "polls_advanced", label: "Advanced polls" },
      { key: "polls_manage", label: "Manage polls" },
      { key: "policy", label: "Company policy" },
    ],
  },
  {
    name: "Helpdesk",
    tileLabel: "Helpdesk",
    to: "/admin/helpdesk",
    icon: Headphones,
    module: "helpdesk",
    rbacModule: "helpdesk",
    children: MODULE_CORE_CHILDREN.helpdesk,
  },
  {
    name: "User Roles",
    tileLabel: "Roles",
    to: "/admin/user-role-management",
    icon: ShieldCheck,
    module: "configuration",
    rbacModule: "user_roles",
    children: [
      { key: "user_role_management", label: "User role management" },
    ],
  },
];

// Accordion child -> screen gate; must mirror the target page's own tab gate.
// `can(screen, action = "view")` checks one screen of the item's rbacModule.
const anyView = (...screens) => (can) => screens.some((s) => can(s));
const ATTENDANCE_ACTION_VIEW_SCREENS = [
  "att_act_change_status",
  "att_act_apply_leave",
  "att_act_change_shift",
  "att_act_change_wo",
  "att_act_regularise_checks",
  "att_act_short_leave",
  "att_act_lop_reversal",
  "att_act_leave_adjustment",
  "att_act_leave_balance",
  "att_act_comp_off_ledger",
  "view_edit_checks",
  "att_act_on_duty",
  "att_act_overtime",
  "att_act_restricted_holiday",
];
const ATTENDANCE_VIEW_SCREENS = [
  "attendance_dashboard",
  "attendance_history",
  "mark_attendance",
  "leave_requests",
  "regularization",
  "attendance_import",
  "comp_off_opening_import",
  "attendance_lop_reversal_import",
  "comp_off_ledger_report",
  "process_attendance",
  "attendance_general_settings",
  "attendance_weekly_off_settings",
  "attendance_holiday_settings",
  "attendance_leave_settings",
  "attendance_shift_settings",
  "attendance_policy_settings",
  "attendance_code_mapping",
  "attendance_devices",
  ...ATTENDANCE_ACTION_VIEW_SCREENS,
];
const NAV_CHILD_GATES = {
  configuration: { enterprise: anyView("enterprise"), masters: anyView("masters") },
  employee: {
    dashboard: anyView(
      "employee_dashboard",
      "employee_list",
      "emp_act_view_employee",
      "emp_act_generate_letter",
      "emp_act_confirmation",
      "emp_act_transfer",
      "emp_act_resignation"
    ),
    employees: anyView(
      "employee_list",
      "emp_act_view_employee",
      "emp_act_generate_letter",
      "emp_act_confirmation",
      "emp_act_transfer",
      "emp_act_resignation"
    ),
    onboard: (can) =>
      can("employee_list") &&
      can("onboard_employee", "create"),
  },
  attendance: {
    dashboard: anyView(...ATTENDANCE_VIEW_SCREENS),
    process: anyView("process_attendance", ...ATTENDANCE_ACTION_VIEW_SCREENS),
  },
  payroll: {
    dashboard: anyView(
      "payroll_dashboard",
      "assigned_structure",
      "run_payroll",
      "pay_act_assigned_structure",
      "pay_act_salary_revision",
      "pay_act_update_statutory",
      "pay_act_tax_planning",
      "pay_act_loan",
      "pay_act_leave_encashment",
      "pay_act_advance",
      "pay_act_reimbursement",
      "pay_act_reimb_optins",
      "pay_act_reimb_claims",
      "pay_act_reimb_ledger",
      "pay_act_reimb_year_end",
      "pay_act_process_fnf"
    ),
    run_payroll: anyView(
      "payroll_dashboard",
      "assigned_structure",
      "run_payroll",
      "pay_act_assigned_structure",
      "pay_act_salary_revision",
      "pay_act_update_statutory",
      "pay_act_tax_planning",
      "pay_act_loan",
      "pay_act_leave_encashment",
      "pay_act_advance",
      "pay_act_reimbursement",
      "pay_act_reimb_optins",
      "pay_act_reimb_claims",
      "pay_act_reimb_ledger",
      "pay_act_reimb_year_end",
      "pay_act_process_fnf"
    ),
    statutory_filing: anyView("statutory_filing", "form24q_filing"),
  },
  hiring: {
    dashboard: anyView("dashboard"),
    jobs: anyView("mrf_jobs"),
    candidates: anyView("candidates"),
    ats: anyView("ats"),
    referrals: anyView("referrals"),
  },
  assets: {
    dashboard: anyView("asset_dashboard"),
    register: anyView("asset_list"),
    requests: anyView("asset_requests"),
    operations: anyView("asset_operations"),
  },
  communication: {
    flash_news: anyView("flash_news"),
    announcement: anyView("announcements"),
    polls_advanced: anyView("polls"),
    polls_manage: anyView("polls"),
    policy: anyView("company_policy"),
  },
  helpdesk: {
    dashboard: anyView("tickets", "helpdesk_config"),
    tickets: anyView("tickets"),
    catalog: anyView("helpdesk_config"),
  },
  user_roles: {
    user_role_management: anyView("role_list", "screen_access", "data_access"),
  },
};

const WORKSPACE_NAV_ICONS = {
  reports: FileBarChart,
  import_export: ArrowLeftRight,
  checklist: ListChecks,
  settings: Settings,
  help: CircleHelp,
};

const arraysMatch = (a = [], b = []) =>
  a.length === b.length && a.every((item, idx) => item === b[idx]);

const Sidebar = memo(function Sidebar({ setCurrentPage, darkMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    hasAnyModuleAccess,
    hasScreenAccess,
    isAdmin,
    snapshot,
    roleCode: permissionsRoleCode,
  } = usePermissions();
  const showMyTeam = useMyTeamNavVisibility();
  const [companyData, setCompanyData] = useState(null);
  // Left rail is always open — mid-edge collapse control removed
  const [leftOpen] = useState(true);
  const [shellMode, setShellMode] = useState(() => {
    try {
      const v = localStorage.getItem("adminShellMode");
      return v === "admin" || v === "employee" ? v : "employee";
    } catch {
      return "employee";
    }
  });
  const [expandedAdminKey, setExpandedAdminKey] = useState(null);
  const [expandedDeskKey, setExpandedDeskKey] = useState(null);
  const [flippedRailKey, setFlippedRailKey] = useState(
    () => shellFlipStore.railKey
  );
  const [flippedSecondaryKey, setFlippedSecondaryKey] = useState(
    () => shellFlipStore.secondaryKey
  );

  const setRailFlipPersistent = useCallback((keyOrUpdater) => {
    setFlippedRailKey((prev) => {
      const next =
        typeof keyOrUpdater === "function" ? keyOrUpdater(prev) : keyOrUpdater;
      shellFlipStore.railKey = next;
      return next;
    });
  }, []);

  const setSecondaryFlipPersistent = useCallback((keyOrUpdater) => {
    setFlippedSecondaryKey((prev) => {
      const next =
        typeof keyOrUpdater === "function" ? keyOrUpdater(prev) : keyOrUpdater;
      shellFlipStore.secondaryKey = next;
      return next;
    });
  }, []);
  const [expandedPanels, setExpandedPanels] = useState({
    usefulLinks: false,
  });
  const [selectedTypeByPanel, setSelectedTypeByPanel] = useState({
    review: ALL_DESK_TYPES,
    approvals: ALL_DESK_TYPES,
  });
  const [statusModal, setStatusModal] = useState(() => {
    try {
      const path = window.location.pathname || "";
      if (path === DESK_ROUTES.approvals) {
        return { open: true, panelKey: "approvals", status: "Pending" };
      }
      if (path === DESK_ROUTES.review) {
        return { open: true, panelKey: "review", status: "Pending" };
      }
    } catch {
      /* ignore */
    }
    return { open: false, panelKey: "review", status: "Pending" };
  });
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [quickActionRequests, setQuickActionRequests] = useState(() => {
    try {
      // Leave requests are loaded from the API (workflow-backed), not localStorage.
      return loadQuickActionRequests().filter(
        (item) => !isLeaveActionLabel(item?.actionLabel)
      );
    } catch {
      return [];
    }
  });
  const [leaveDeskRequests, setLeaveDeskRequests] = useState([]);
  const [onboardingDeskRequests, setOnboardingDeskRequests] = useState([]);
  const [onboardingMyRequests, setOnboardingMyRequests] = useState([]);
  const [lifecycleDeskRequests, setLifecycleDeskRequests] = useState([]);
  const [lifecycleMyRequests, setLifecycleMyRequests] = useState([]);
  const [helpdeskDeskRequests, setHelpdeskDeskRequests] = useState([]);
  const [flexiDeskRequests, setFlexiDeskRequests] = useState([]);
  const [selectedStatusByPanel, setSelectedStatusByPanel] = useState({
    review: "Pending",
    approvals: "Pending",
  });
  const [statusDeskShowForm, setStatusDeskShowForm] = useState(false);
  const [resignationFormOpen, setResignationFormOpen] = useState(false);
  const [flexiReimbModal, setFlexiReimbModal] = useState({
    open: false,
    mode: "declare",
  });
  const [statusDeskContext, setStatusDeskContext] = useState(null);
  const [quickActionSubmitting, setQuickActionSubmitting] = useState(false);
  const quickActionSubmitLockRef = useRef(false);
  const [statusActionModal, setStatusActionModal] = useState({
    open: false,
    requestIds: [],
    nextStatus: "",
    reason: "",
  });
  const [statusActionSubmitting, setStatusActionSubmitting] = useState(false);
  const [quickActionDraft, setQuickActionDraft] = useState({
    date: "",
    fromDate: "",
    toDate: "",
    shift: "",
    currentShift: "",
    leaveType: "",
    leaveDuration: "full_day",
    inTime: "",
    outTime: "",
    startTime: "",
    endTime: "",
    resignationReason: "",
    resignationDate: "",
    lastWorkingDate: "",
    reason: "",
    notes: "",
    ticketType: "",
    ticketCategory: "",
    ticketSubcategory: "",
    ticketPriority: "medium",
    ticketSubject: "",
    amount: "",
    investmentSection: "",
    financialYear: "",
    taxRegime: "new",
    positionTitle: "",
    positionsCount: "1",
    requiredByDate: "",
    requestDepartment: "",
    requestDesignation: "",
  });
  const [usefulLinksAudience, setUsefulLinksAudience] = useState(() => {
    const v = localStorage.getItem("adminUsefulLinksAudience");
    return v === "employer" ? "employer" : "employee";
  });
  const maxDateToday = useMemo(() => getMaxDateInputValue(), []);

  useEffect(() => {
    const navRbacModules = navItems.map((item) => item.rbacModule);
    if (!arraysMatch(navRbacModules, TOP_NAV_MODULE_KEYS)) {
      console.warn("[RBAC] Sidebar navItems do not match catalog top-nav modules.", {
        sidebar: navRbacModules,
        catalog: TOP_NAV_MODULE_KEYS,
      });
    }
  }, []);

  const [woAccessTick, setWoAccessTick] = useState(0);

  // Prefetch Weekly Off policies so Change WO request visibility can resolve overridePolicy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [woRes, mapRes] = await Promise.all([
          attendanceStructureService.getWeeklyOffPolicies().catch(() => null),
          attendanceStructureService.getEmployeeWeeklyOffPolicies().catch(() => null),
        ]);
        if (cancelled) return;
        cacheWeeklyOffStructureData(
          Array.isArray(woRes?.data) ? woRes.data : [],
          mapRes?.data && typeof mapRes.data === "object" ? mapRes.data : {}
        );
        setWoAccessTick((t) => t + 1);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("adminUsefulLinksAudience", usefulLinksAudience);
  }, [usefulLinksAudience]);

  // Backfill RM person names on pending desk rows (RH / OD / etc.) so Approvals
  // matches Leave: "Pending · [RM name]" and Activity shows the RM, not the employee.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const enriched = await enrichPendingDeskRequestsWithRmNames(quickActionRequests);
        if (cancelled || !Array.isArray(enriched)) return;
        const changed =
          enriched.length !== quickActionRequests.length ||
          enriched.some((row, idx) => {
            const prev = quickActionRequests[idx];
            return (
              row?.pendingAssigneeDisplay !== prev?.pendingAssigneeDisplay ||
              row?.pendingWith !== prev?.pendingWith ||
              JSON.stringify(row?.workflowTrail) !== JSON.stringify(prev?.workflowTrail)
            );
          });
        if (changed) setQuickActionRequests(enriched);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only on mount / when request ids change — avoid loops on display patches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickActionRequests
      .map((r) => `${r?.requestId}:${r?.status}`)
      .join("|"),
  ]);

  // Pull engine-created Comp Off credit / OT / Lock / Unlock pending rows into Approvals desk
  useEffect(() => {
    let cancelled = false;
    const refreshDesk = async () => {
      try {
        const { hydrateCompOffOtDeskFromServer } = await import(
          "../../lib/compOffOtNotify"
        );
        const { loadQuickActionRequests } = await import(
          "../../lib/attendanceDeskRequests"
        );
        await hydrateCompOffOtDeskFromServer();
        if (cancelled) return;
        const next = loadQuickActionRequests() || [];
        setQuickActionRequests(next.filter((item) => !isLeaveActionLabel(item?.actionLabel)));
      } catch {
        /* ignore */
      }
    };
    refreshDesk();
    window.addEventListener("admin:quick-action-submitted", refreshDesk);
    window.addEventListener("hrms-attendance-quick-request-updated", refreshDesk);
    return () => {
      cancelled = true;
      window.removeEventListener("admin:quick-action-submitted", refreshDesk);
      window.removeEventListener("hrms-attendance-quick-request-updated", refreshDesk);
    };
  }, []);

  useEffect(() => {
    const storedData = getAuthJson("companyData", null);
    setCompanyData(storedData);

    const handleCompanyUpdate = (e) => setCompanyData(e.detail);
    const onStorage = (e) => {
      if (e.key === "companyData") {
        try {
          setCompanyData(e.newValue ? JSON.parse(e.newValue) : getAuthJson("companyData", null));
        } catch {
          setCompanyData(null);
        }
      }
    };
    window.addEventListener("companyDataUpdated", handleCompanyUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("companyDataUpdated", handleCompanyUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("adminRightSidebarOpen", "false");
  }, []);
  useEffect(() => {
    localStorage.setItem("adminLeftSidebarOpen", String(leftOpen));
  }, [leftOpen]);
  useEffect(() => {
    try {
      localStorage.setItem("adminShellMode", shellMode);
    } catch {
      /* ignore */
    }
  }, [shellMode]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--admin-right-sidebar-width", "0px");
    root.style.setProperty("--admin-left-sidebar-width", `${LEFT_SIDEBAR_WIDTH}px`);
    const path = location.pathname || "";
    const merged =
      shellMode === "admin" &&
      /^\/admin\/(configuration|employee|attendance|payroll|communication|helpdesk|user-role-management)(\/|$)/.test(
        path
      );
    root.classList.toggle("pace-shell-aside-merged", merged);
    const secondaryH =
      shellMode === "admin" ? `${SECONDARY_NAV_HEIGHT}px` : "0px";
    root.style.setProperty("--admin-secondary-nav-height", secondaryH);
    root.style.setProperty("--admin-left-sidebar-width", `${LEFT_SIDEBAR_WIDTH}px`);
    return () => {
      root.style.setProperty("--admin-right-sidebar-width", "0px");
      root.style.setProperty("--admin-left-sidebar-width", "0px");
      root.style.setProperty("--admin-secondary-nav-height", "0px");
      root.classList.remove("pace-shell-aside-merged");
    };
  }, [shellMode, location.pathname]);

  // Flyout is click-to-open only — do not keep it expanded from the current route.
  useEffect(() => {
    if (shellMode !== "admin") return;
    const path = location.pathname || "";
    if (path.startsWith("/admin/workspace/")) {
      setExpandedAdminKey(null);
    }
  }, [location.pathname, shellMode]);

  // Open embedded Request / Approvals desk from URL (+ type under parent accordion)
  useEffect(() => {
    const path = location.pathname || "";
    if (path === DESK_ROUTES.review || path === DESK_ROUTES.approvals) {
      const panelKey = path === DESK_ROUTES.approvals ? "approvals" : "review";
      setExpandedDeskKey(panelKey);
      setStatusModal((prev) =>
        prev.open && prev.panelKey === panelKey
          ? prev
          : { open: true, panelKey, status: "Pending" }
      );
      try {
        const type = new URLSearchParams(location.search).get("type");
        if (type) {
          setSelectedTypeByPanel((prev) => ({
            ...prev,
            [panelKey]: type === "All" ? ALL_DESK_TYPES : type,
          }));
        } else {
          setSelectedTypeByPanel((prev) => ({
            ...prev,
            [panelKey]: ALL_DESK_TYPES,
          }));
        }
      } catch {
        /* ignore */
      }
      return;
    }
    setStatusModal((prev) => (prev.open ? { ...prev, open: false } : prev));
  }, [location.pathname, location.search]);

  const getModuleStatus = (moduleName) => ({
    isEnabled: isCompanyModuleEnabled(moduleName, companyData),
  });

  const getQuickPanelUserIds = () => {
    const currentUser = getAuthJson("currentUser");
    return {
      userId: currentUser?.id || null,
      employeeId:
        Number(getAuthItem("employeeId")) ||
        Number(currentUser?.employeeId) ||
        null,
    };
  };

  const isOwnQuickRequest = (request, userId, employeeId) => {
    // Backend my-requests desks are already scoped to the signed-in user.
    if (request?.deskMode === "request" && request?.source === "api") return true;
    if (request?.panelKey === "review" && request?.source === "api" && request?.lifecycleKind) {
      return true;
    }
    if (isOnboardingDeskRequest(request) && request?.deskMode !== "request") return false;
    if (isLifecycleDeskRequest(request) && request?.deskMode !== "request") return false;
    if (isLeaveDeskRequest(request)) {
      if (employeeId && Number(request.employeeId) === Number(employeeId)) return true;
      if (userId && request.userId === userId) return true;
      return false;
    }
    const isFinalization =
      request?.actionLabel === "Attendance Lock" ||
      request?.actionLabel === "Attendance Unlock" ||
      request?.actionLabel === "Attendance Finalization";
    if (isFinalization) {
      if (isAdmin) return true;
      if (userId && (request.userId === userId || request.submittedByUserId === userId)) return true;
      if (employeeId && Number(request.employeeId) === Number(employeeId)) return true;
    }
    if (userId && (request.userId === userId || request.submittedByUserId === userId)) return true;
    if (employeeId && Number(request.employeeId) === Number(employeeId)) return true;
    return false;
  };

  const fetchLeaveDeskRequests = useCallback(async () => {
    try {
      const { employeeId, userId } = getQuickPanelUserIds();
      const [approvalsRes, pendingRes] = await Promise.all([
        attendanceStructureService.getLeaveApprovalsDesk().catch(() => null),
        attendanceStructureService.getPendingLeaveRequestsForUser().catch(() => ({
          data: [],
        })),
      ]);
      let mine = [];
      if (employeeId) {
        const mineRes = await attendanceStructureService.getLeaveRequests({
          employeeId,
        });
        mine = Array.isArray(mineRes?.data) ? mineRes.data : [];
      }
      const approvalsDesk = Array.isArray(approvalsRes?.data)
        ? approvalsRes.data
        : [];
      const pendingMyAction = Array.isArray(pendingRes?.data)
        ? pendingRes.data
        : [];
      setLeaveDeskRequests(
        buildLeaveDeskItems({
          mine,
          pendingMyAction,
          approvalsDesk,
          employeeId,
          userId,
        })
      );
    } catch (error) {
      console.warn("Failed to load leave request desk:", error);
    }
  }, []);

  const fetchFlexiDeskRequests = useCallback(async () => {
    try {
      const { employeeId, userId } = getQuickPanelUserIds();
      const [claimsRes, declRes] = await Promise.all([
        flexiBenefitsService.listClaims({}).catch(() => ({ data: [] })),
        flexiBenefitsService.listDeclarations({}).catch(() => ({ data: [] })),
      ]);
      setFlexiDeskRequests(
        buildFlexiReimbursementDeskItems({
          claims: Array.isArray(claimsRes?.data) ? claimsRes.data : [],
          declarations: Array.isArray(declRes?.data) ? declRes.data : [],
          employeeId,
          userId,
        })
      );
    } catch (error) {
      console.warn("Failed to load reimbursement desk:", error);
    }
  }, []);

  const fetchOnboardingDeskRequests = useCallback(async () => {
    try {
      const [approvalsRes, myRes] = await Promise.all([
        employeeOnboardingService.listApprovalsDesk(),
        employeeOnboardingService.listMyRequestsDesk().catch(() => ({ data: [] })),
      ]);
      const rows = collapseDuplicateOnboardingChecklistRows(
        Array.isArray(approvalsRes?.data) ? approvalsRes.data : []
      );
      const mapped = rows.map((row) => {
        if (row.deskMode === "checklist") {
          return mapOnboardingChecklistToDeskItem(row);
        }
        const requestId = `onboarding_${row.id}${row.notifyOnlyFyi ? "_notify" : ""}`;
        return mapOnboardingRequestToDeskItem(row, {
          notifyOnlyFyi: !!row.notifyOnlyFyi,
          acknowledged: isOnboardingNotifyAcknowledged(requestId),
        });
      });
      setOnboardingDeskRequests(
        ensureUniqueDeskRequests(collapseDuplicateOnboardingChecklistRows(mapped))
      );
      syncNotifyOnlyNavbarFromDesk(mapped);

      const myRows = Array.isArray(myRes?.data) ? myRes.data : [];
      setOnboardingMyRequests(
        ensureUniqueDeskRequests(myRows.map(mapOnboardingMyRequestToDeskItem))
      );
    } catch (error) {
      console.warn("Failed to load onboarding approval desk:", error);
    }
  }, []);

  const fetchLifecycleDeskRequests = useCallback(async () => {
    try {
      const [
        confApprovals,
        confMine,
        xferApprovals,
        xferMine,
        sepApprovals,
        sepMine,
      ] = await Promise.all([
        employeeConfirmationService.listApprovalsDesk().catch(() => ({ data: [] })),
        employeeConfirmationService.listMyRequestsDesk().catch(() => ({ data: [] })),
        employeeTransferService.listApprovalsDesk().catch(() => ({ data: [] })),
        employeeTransferService.listMyRequestsDesk().catch(() => ({ data: [] })),
        employeeSeparationService.listApprovalsDesk().catch(() => ({ data: [] })),
        employeeSeparationService.listMyRequestsDesk().catch(() => ({ data: [] })),
      ]);

      const approvalRows = [
        ...(Array.isArray(confApprovals?.data) ? confApprovals.data : []),
        ...(Array.isArray(xferApprovals?.data) ? xferApprovals.data : []),
        ...(Array.isArray(sepApprovals?.data) ? sepApprovals.data : []),
      ];
      setLifecycleDeskRequests(mapLifecycleDeskApiRows(approvalRows));

      const myRows = [
        ...(Array.isArray(confMine?.data) ? confMine.data : []),
        ...(Array.isArray(xferMine?.data) ? xferMine.data : []),
        ...(Array.isArray(sepMine?.data) ? sepMine.data : []),
      ];
      setLifecycleMyRequests(mapLifecycleDeskApiRows(myRows));
    } catch (error) {
      console.warn("Failed to load lifecycle approval desks:", error);
    }
  }, []);

  useEffect(() => {
    fetchOnboardingDeskRequests();
    fetchLifecycleDeskRequests();
    const onSubmitted = () => {
      fetchOnboardingDeskRequests();
      fetchLifecycleDeskRequests();
    };
    window.addEventListener("admin:onboarding-submitted", onSubmitted);
    window.addEventListener("admin:lifecycle-request-changed", onSubmitted);
    return () => {
      window.removeEventListener("admin:onboarding-submitted", onSubmitted);
      window.removeEventListener("admin:lifecycle-request-changed", onSubmitted);
    };
  }, [fetchOnboardingDeskRequests, fetchLifecycleDeskRequests]);

  const fetchHelpdeskDeskRequests = useCallback(async () => {
    try {
      const res = await helpdeskService.listDeskTickets();
      const pack = res?.data || {};
      const mapped = buildHelpdeskDeskItems({
        mine: pack.mine || [],
        pendingMyAction: pack.pendingMyAction || [],
      });
      setHelpdeskDeskRequests(mapped);
    } catch (error) {
      console.warn("Failed to load helpdesk desk:", error);
    }
  }, []);

  useEffect(() => {
    fetchHelpdeskDeskRequests();
    const onChanged = () => fetchHelpdeskDeskRequests();
    window.addEventListener("admin:helpdesk-ticket-changed", onChanged);
    window.addEventListener("admin:quick-action-submitted", onChanged);
    return () => {
      window.removeEventListener("admin:helpdesk-ticket-changed", onChanged);
      window.removeEventListener("admin:quick-action-submitted", onChanged);
    };
  }, [fetchHelpdeskDeskRequests]);

  useEffect(() => {
    fetchLeaveDeskRequests();
    const onChanged = () => fetchLeaveDeskRequests();
    const onSocketDesk = (payload) => {
      try {
        const me = getAuthJson("currentUser");
        const myCompanyId = me?.companyId ?? me?.company_id;
        if (
          payload?.companyId != null &&
          myCompanyId != null &&
          Number(payload.companyId) !== Number(myCompanyId)
        ) {
          return;
        }
      } catch {
        /* ignore */
      }
      fetchLeaveDeskRequests();
    };
    window.addEventListener("admin:quick-action-submitted", onChanged);
    window.addEventListener("admin:leave-request-changed", onChanged);
    if (socket?.on) {
      socket.on("leave-request-updated", onSocketDesk);
      socket.on("workflow-desk-updated", onSocketDesk);
    }
    return () => {
      window.removeEventListener("admin:quick-action-submitted", onChanged);
      window.removeEventListener("admin:leave-request-changed", onChanged);
      if (socket?.off) {
        socket.off("leave-request-updated", onSocketDesk);
        socket.off("workflow-desk-updated", onSocketDesk);
      }
    };
  }, [fetchLeaveDeskRequests]);

  useEffect(() => {
    fetchFlexiDeskRequests();
    const onChanged = () => fetchFlexiDeskRequests();
    window.addEventListener("admin:quick-action-submitted", onChanged);
    window.addEventListener("admin:flexi-reimbursement-changed", onChanged);
    return () => {
      window.removeEventListener("admin:quick-action-submitted", onChanged);
      window.removeEventListener("admin:flexi-reimbursement-changed", onChanged);
    };
  }, [fetchFlexiDeskRequests]);

  const allDeskRequests = useMemo(
    () =>
      ensureUniqueDeskRequests([
        ...quickActionRequests.filter(
          (r) =>
            !(
              r?.actionLabel === RESIGNATION_REQUEST_LABEL ||
              r?.actionLabel === CONFIRMATION_REQUEST_LABEL ||
              r?.actionLabel === TRANSFER_REQUEST_LABEL ||
              r?.actionLabel === ONBOARDING_REQUEST_LABEL
            )
        ),
        ...leaveDeskRequests,
        ...onboardingDeskRequests,
        ...onboardingMyRequests,
        ...lifecycleDeskRequests,
        ...lifecycleMyRequests,
        ...helpdeskDeskRequests,
        ...flexiDeskRequests,
      ]),
    [
      quickActionRequests,
      leaveDeskRequests,
      onboardingDeskRequests,
      onboardingMyRequests,
      lifecycleDeskRequests,
      lifecycleMyRequests,
      helpdeskDeskRequests,
      flexiDeskRequests,
    ]
  );

  const getRequestsForStatus = useCallback(
    (panelKey, statusValue, selectedType) => {
      const { userId, employeeId } = getQuickPanelUserIds();
      const approvalOnlyLabels = new Set(APPROVAL_ONLY_TYPE_DEFS.map((item) => item.label));
      const isAllTypes = !selectedType || selectedType === ALL_DESK_TYPES;

      const rows = allDeskRequests
        .filter((request) => {
          if (isAllTypes) {
            if (panelKey === "approvals") {
              // Leave/lifecycle use panelKey approvals / deskMode leave.
              // RM-routed desk items (OD, RH, Change Shift, …) stay on panelKey
              // "review" for the requester — still surface them here for the RM.
              if (
                request.panelKey === "approvals" ||
                request.deskMode === "approval" ||
                request.deskMode === "checklist" ||
                request.deskMode === "notify" ||
                request.deskMode === "leave" ||
                request.deskMode === "flexi_claim" ||
                request.deskMode === "flexi_optin"
              ) {
                return true;
              }
              return (
                isRmRoutedActionLabel(request.actionLabel) &&
                (request.source === "sidebar_status_desk" ||
                  request.source === "my_team" ||
                  request.source === "ess_status_edit" ||
                  request.source === "api" ||
                  !request.source ||
                  request.source === "local")
              );
            }
            return request.panelKey === "review" || request.deskMode === "request";
          }
          if (panelKey === "approvals") {
            if (approvalOnlyLabels.has(selectedType)) {
              return (
                request.panelKey === "approvals" &&
                matchesDeskSelectedType(request.actionLabel, selectedType)
              );
            }
            return matchesDeskSelectedType(request.actionLabel, selectedType);
          }
          return (
            request.panelKey === "review" &&
            matchesDeskSelectedType(request.actionLabel, selectedType)
          );
        })
        .filter((request) => {
          return request.status === statusValue;
        })
        .filter((request) => {
          if (panelKey === "approvals") {
            // Change Shift / similar: only the requester's Reporting Manager
            // (or Admin when no RM) — not every ESS user with Approvals access.
            if (
              isRmRoutedActionLabel(request.actionLabel) &&
              (request.source === "sidebar_status_desk" ||
                request.source === "my_team" ||
                request.source === "ess_status_edit" ||
                request.source === "api" ||
                !request.source ||
                request.source === "local")
            ) {
              const me = getAuthJson("currentUser");
              return canActOnRmRoutedApproval(request, {
                employeeId,
                isAdmin,
                customRoleCode:
                  permissionsRoleCode ||
                  snapshot?.roleCode ||
                  me?.customRoleCode ||
                  me?.roleCode ||
                  null,
              });
            }
            return !isOwnQuickRequest(request, userId, employeeId);
          }
          return isOwnQuickRequest(request, userId, employeeId);
        });
      return sortDeskRequestsLatestFirst(rows);
    },
    [allDeskRequests, isAdmin, permissionsRoleCode, snapshot?.roleCode]
  );

  const openStatusModal = (panelKey, status = "Pending", typeLabel = ALL_DESK_TYPES) => {
    // Always land on Pending; type comes from accordion child (or All)
    setSelectedStatusByPanel((prev) => ({
      ...prev,
      [panelKey]: "Pending",
    }));
    setSelectedTypeByPanel((prev) => ({
      ...prev,
      [panelKey]: typeLabel || ALL_DESK_TYPES,
    }));
    setStatusDeskShowForm(false);
    setResignationFormOpen(false);
    setStatusDeskContext(null);
    setSelectedRequestId(null);
    setExpandedDeskKey(panelKey);
    setExpandedAdminKey(null);
    setStatusModal({
      open: true,
      panelKey,
      status: "Pending",
    });
    const deskPath = DESK_ROUTES[panelKey];
    if (deskPath) {
      const typeQ =
        !typeLabel || typeLabel === ALL_DESK_TYPES
          ? "All"
          : typeLabel;
      navigate(`${deskPath}?type=${encodeURIComponent(typeQ)}`);
      setCurrentPage(panelKey === "approvals" ? "Approvals" : "Requests");
    }
  };

  const toggleDeskAccordion = (panelKey) => {
    openStatusModal(panelKey, "Pending", ALL_DESK_TYPES);
  };

  const closeStatusModal = () => {
    setStatusModal((prev) => ({ ...prev, open: false }));
    setSelectedRequestId(null);
    setStatusDeskShowForm(false);
    setResignationFormOpen(false);
    setStatusDeskContext(null);
    if (
      location.pathname === DESK_ROUTES.review ||
      location.pathname === DESK_ROUTES.approvals
    ) {
      navigate("/admin/home");
      setCurrentPage("Home");
    }
  };
  const getFilteredRequests = () => {
    return getRequestsForStatus(
      statusModal.panelKey,
      resolveDeskStatusTab(
        selectedStatusByPanel[statusModal.panelKey] || statusModal.status || "Pending"
      ),
      selectedTypeByPanel[statusModal.panelKey] || ""
    );
  };
  useEffect(() => {
    if (!statusModal.open) return;
    const filtered = getFilteredRequests();
    if (filtered.length === 0) {
      setSelectedRequestId(null);
      return;
    }
    if (!filtered.some((request) => deskRequestIdsEqual(request.requestId, selectedRequestId))) {
      setSelectedRequestId(filtered[0].requestId);
    }
  }, [
    statusModal.open,
    statusModal.panelKey,
    statusModal.status,
    selectedStatusByPanel,
    selectedTypeByPanel,
    allDeskRequests,
    selectedRequestId,
  ]);

  const openLinkedEmployeeFromDesk = (request) => {
    if (!request) return;
    const empCode =
      request.formData?.employeeCode ||
      request.employeeCode ||
      null;
    const employeeId = request.linkedEmployeeId || request.formData?.linkedEmployeeId || null;
    if (!empCode && !employeeId) {
      toast.error("Employee record is not linked yet for this task.");
      return;
    }
    // Close desk without navigating to Home (closeStatusModal forces /admin/home).
    setStatusModal((prev) => ({ ...prev, open: false }));
    setSelectedRequestId(null);
    navigate("/admin/employee");
    setCurrentPage("Employee");
    window.dispatchEvent(
      new CustomEvent("admin:open-employee-record", {
        detail: {
          empCode,
          employeeId,
          readOnly: true,
          fromOnboardingTask: request.deskMode === "checklist",
        },
      })
    );
  };

  const continueOnboardingDraftFromDesk = (request) => {
    if (!request) return;
    const requestId =
      request.onboardingRequestId ||
      (String(request.requestId || "").match(/onboarding_(?:req_)?(\d+)/i)?.[1] ??
        null);
    if (!requestId) {
      toast.error("Missing onboarding draft id");
      return;
    }
    try {
      sessionStorage.setItem(
        "pacehrm:continueOnboardingDraftId",
        String(requestId)
      );
    } catch {
      /* ignore */
    }
    // Keep desk closed without bounce-to-Home; open Employee onboard via query.
    setStatusModal((prev) => ({ ...prev, open: false }));
    setSelectedRequestId(null);
    navigate(
      `/admin/employee?continueOnboarding=${encodeURIComponent(String(requestId))}`
    );
    setCurrentPage("Employee");
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("admin:continue-onboarding-draft", {
          detail: { onboardingRequestId: requestId },
        })
      );
    }, 250);
  };
  const applyStatusSelection = (status) => {
    setSelectedStatusByPanel((prev) => ({
      ...prev,
      [statusModal.panelKey]: status,
    }));
    const payload = {
      panelKey: statusModal.panelKey,
      status,
      selectedType: selectedTypeByPanel[statusModal.panelKey] || "",
      selectedAt: Date.now(),
      source: "sidebar_quick_status_modal",
    };
    try {
      localStorage.setItem("adminQuickActionStatus", JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }
    window.dispatchEvent(new CustomEvent("admin:quick-action-status", { detail: payload }));
  };
  const updateQuickRequestStatus = (requestId, nextStatus, actionReason = "") => {
    const currentUser = getAuthJson("currentUser", null);
    const firstName = currentUser?.name || "";
    const lastName = currentUser?.lastName || "";
    const actorName = [firstName, lastName].filter(Boolean).join(" ") || "Approver";
    const now = Date.now();
    setQuickActionRequests((prev) => {
      const updated = prev.map((request) => {
        if (request.requestId !== requestId) return request;
        const trail = Array.isArray(request.workflowTrail)
          ? [...request.workflowTrail]
          : [];
        // Replace Current/Pending awaiting step instead of appending a duplicate person row
        const openIdx = trail.findIndex((s) => {
          const st = String(s?.status || "").toLowerCase();
          return st === "current" || st === "pending";
        });
        const entry = {
          status: nextStatus,
          actor: actorName,
          displayText: actorName,
          actedAt: now,
          reason: actionReason,
        };
        if (openIdx >= 0) {
          trail[openIdx] = { ...trail[openIdx], ...entry };
        } else {
          trail.push(entry);
        }
        return {
          ...request,
          status: nextStatus,
          updatedAt: now,
          pendingWith:
            nextStatus === "Pending"
              ? request.pendingWith || "Reporting Manager"
              : null,
          pendingAssigneeDisplay:
            nextStatus === "Pending" ? request.pendingAssigneeDisplay : null,
          pendingRole: nextStatus === "Pending" ? request.pendingRole : null,
          pendingLabel: nextStatus === "Pending" ? request.pendingLabel : null,
          lastDecision:
            nextStatus === "Pending"
              ? null
              : {
                  status: nextStatus,
                  actorName,
                  actedAt: now,
                  reason: actionReason || null,
                },
          actionRemarks: actionReason || request.actionRemarks || null,
          workflowTrail: trail,
        };
      });
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(updated));
      } catch {
        // ignore storage failures
      }
      return updated;
    });
  };

  const updateQuickRequestStatusBulk = (requestIds, nextStatus, actionReason = "") => {
    const idSet = new Set(requestIds);
    const currentUser = getAuthJson("currentUser", null);
    const firstName = currentUser?.name || "";
    const lastName = currentUser?.lastName || "";
    const actorName = [firstName, lastName].filter(Boolean).join(" ") || "Approver";
    const now = Date.now();
    setQuickActionRequests((prev) => {
      const updated = prev.map((request) => {
        if (!idSet.has(request.requestId)) return request;
        const trail = Array.isArray(request.workflowTrail)
          ? [...request.workflowTrail]
          : [];
        const openIdx = trail.findIndex((s) => {
          const st = String(s?.status || "").toLowerCase();
          return st === "current" || st === "pending";
        });
        const entry = {
          status: nextStatus,
          actor: actorName,
          displayText: actorName,
          actedAt: now,
          reason: actionReason,
        };
        if (openIdx >= 0) {
          trail[openIdx] = { ...trail[openIdx], ...entry };
        } else {
          trail.push(entry);
        }
        return {
          ...request,
          status: nextStatus,
          updatedAt: now,
          pendingWith:
            nextStatus === "Pending"
              ? request.pendingWith || "Reporting Manager"
              : null,
          pendingAssigneeDisplay:
            nextStatus === "Pending" ? request.pendingAssigneeDisplay : null,
          pendingRole: nextStatus === "Pending" ? request.pendingRole : null,
          pendingLabel: nextStatus === "Pending" ? request.pendingLabel : null,
          lastDecision:
            nextStatus === "Pending"
              ? null
              : {
                  status: nextStatus,
                  actorName,
                  actedAt: now,
                  reason: actionReason || null,
                },
          actionRemarks: actionReason || request.actionRemarks || null,
          workflowTrail: trail,
        };
      });
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(updated));
      } catch {
        // ignore storage failures
      }
      return updated;
    });
  };
  const openStatusActionModal = (requestId, nextStatus) => {
    const request = findDeskRequest(allDeskRequests, requestId);

    // Notify-only onboarding FYI: acknowledge without Approve API / reason dialog
    if (request?.notifyOnlyFyi && (nextStatus === "Acknowledged" || nextStatus === "Approved")) {
      acknowledgeOnboardingNotify(request.requestId);
      setOnboardingDeskRequests((prev) =>
        prev.map((item) =>
          item.requestId === request.requestId
            ? {
                ...item,
                status: "Approved",
                readOnlyApproval: true,
                pendingWith: "Acknowledged (notification only)",
              }
            : item
        )
      );
      toast.success("Marked as viewed — no approval action required");
      setSelectedStatusByPanel((prev) => ({ ...prev, approvals: "Approved" }));
      setStatusModal((prev) => ({ ...prev, status: "Approved" }));
      setSelectedRequestId(null);
      return;
    }

    const actionTitle =
      nextStatus === "Submitted"
        ? "Submit for approval"
        : nextStatus === "CancelRequested"
        ? "Request cancellation"
        : nextStatus === "Undo"
        ? "Recall"
        : request?.deskMode === "checklist"
        ? nextStatus === "Approved"
          ? "Done"
          : nextStatus === "Rejected"
            ? "Reject"
            : nextStatus
        : nextStatus;
    setStatusActionModal({
      open: true,
      requestIds: [requestId],
      nextStatus,
      actionTitle,
      reason: "",
    });
  };

  const openBulkStatusActionModal = (requestIds, nextStatus) => {
    if (!Array.isArray(requestIds) || requestIds.length === 0) return;
    setStatusActionModal({
      open: true,
      requestIds,
      nextStatus,
      actionTitle:
        nextStatus === "Submitted" ? "Submit for approval" : nextStatus,
      reason: "",
    });
  };
  const closeStatusActionModal = () => {
    setStatusActionSubmitting(false);
    setStatusActionModal({
      open: false,
      requestIds: [],
      nextStatus: "",
      actionTitle: "",
      reason: "",
    });
  };
  const confirmStatusAction = async () => {
    const reason = (statusActionModal.reason || "").trim();
    const nextStatus = statusActionModal.nextStatus;
    const remarksOptional = nextStatus === "Submitted" || nextStatus === "Acknowledged";
    if (statusActionSubmitting) return;
    if ((!remarksOptional && !reason) || reason.length > 250) return;
    setStatusActionSubmitting(true);
    try {
    const requestIds = statusActionModal.requestIds;
    const onboardingChecklistIds = requestIds.filter((id) =>
      String(id).startsWith("onboarding_chk_")
    );
    const onboardingApprovalIds = requestIds.filter(
      (id) =>
        (String(id).startsWith("onboarding_") || String(id).startsWith("onboarding_req_")) &&
        !String(id).startsWith("onboarding_chk_")
    );
    const helpdeskIds = requestIds.filter((id) => String(id).startsWith("helpdesk_"));
    const lifecycleIds = requestIds.filter((id) => {
      const s = String(id);
      return (
        s.startsWith("confirmation_") ||
        s.startsWith("transfer_") ||
        s.startsWith("separation_")
      );
    });
    const localIds = requestIds.filter((id) => {
      const s = String(id);
      return (
        !s.startsWith("onboarding_") &&
        !s.startsWith("helpdesk_") &&
        !s.startsWith("confirmation_") &&
        !s.startsWith("transfer_") &&
        !s.startsWith("separation_")
      );
    });

    const refreshLifecycleDesks = async () => {
      await Promise.all([fetchOnboardingDeskRequests(), fetchLifecycleDeskRequests()]);
    };

    if (
      onboardingChecklistIds.length > 0 &&
      (nextStatus === "Approved" || nextStatus === "Rejected")
    ) {
      for (const requestId of onboardingChecklistIds) {
        const request = findDeskRequest(allDeskRequests, requestId);
        if (!request?.onboardingRequestId || !request?.checklistTaskId) continue;
        try {
          if (nextStatus === "Approved") {
            await employeeOnboardingService.completeChecklistTask(
              request.onboardingRequestId,
              request.checklistTaskId,
              reason
            );
            toast.success("Onboarding task marked Done");
          } else {
            const result = await employeeOnboardingService.rejectChecklistTask(
              request.onboardingRequestId,
              request.checklistTaskId,
              reason
            );
            toast.success("Task rejected");
            if (result?.emailSent) {
              toast.success("Candidate emailed to complete profile");
            } else if (result?.emailError) {
              toast.error(`Email not sent: ${result.emailError}`);
            }
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Action failed");
        }
      }
      await fetchOnboardingDeskRequests();
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        approvals: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setSelectedRequestId(null);
    }

    if (
      onboardingApprovalIds.length > 0 &&
      (nextStatus === "Approved" ||
        nextStatus === "Rejected" ||
        nextStatus === "Submitted")
    ) {
      for (const requestId of onboardingApprovalIds) {
        const request = findDeskRequest(allDeskRequests, requestId);
        if (!request?.onboardingRequestId) continue;
        if (request.notifyOnlyFyi) {
          acknowledgeOnboardingNotify(request.requestId);
          toast.success("Marked as viewed — no approval action required");
          continue;
        }
        try {
          if (nextStatus === "Submitted") {
            const result = await employeeOnboardingService.submitRequest(
              request.onboardingRequestId
            );
            applyOnboardingNotifications(result?.notifications);
            if (result?.autoApproved || result?.alreadyCompleted) {
              toast.success(
                result?.notifyOnlyAutoApproved
                  ? "Employee created — reporting manager notified"
                  : "Employee onboarded successfully"
              );
            } else {
              const pending =
                result?.data?.pendingLabel || result?.data?.pendingRole || "approver";
              toast.success(`Submitted for approval — pending with ${pending}`);
            }
          } else if (nextStatus === "Approved") {
            const result = await employeeOnboardingService.approveRequest(
              request.onboardingRequestId,
              reason
            );
            applyOnboardingNotifications(result?.notifications);
            if (result?.employee) {
              toast.success("Employee onboarded and created successfully");
            } else {
              toast.success("Onboarding step approved");
            }
          } else {
            const result = await employeeOnboardingService.rejectRequest(
              request.onboardingRequestId,
              reason
            );
            applyOnboardingNotifications(result?.notifications);
            toast.success("Onboarding request rejected");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Action failed");
        }
      }
      await fetchOnboardingDeskRequests();
    }

    if (lifecycleIds.length > 0 && (nextStatus === "Approved" || nextStatus === "Rejected")) {
      for (const requestId of lifecycleIds) {
        const request = findDeskRequest(allDeskRequests, requestId);
        if (!request || request.deskMode === "request") continue;

        const kind = request.lifecycleKind;
        const service =
          kind === "confirmation"
            ? employeeConfirmationService
            : kind === "transfer"
              ? employeeTransferService
              : kind === "separation"
                ? employeeSeparationService
                : null;
        if (!service) continue;

        const id =
          request.confirmationRequestId ||
          request.transferRequestId ||
          request.separationRequestId;
        if (!id) continue;

        try {
          if (request.deskMode === "checklist") {
            const itemId = request.checklistItemId || request.checklistTaskId;
            if (!itemId) continue;
            if (nextStatus === "Approved") {
              await service.completeChecklistItem(id, itemId, {
                remarks: reason,
                comments: reason,
              });
              toast.success("Checklist task completed");
            } else {
              toast.error("Reject checklist from the request detail in Employee Insights");
            }
            continue;
          }

          if (request.notifyOnlyFyi) {
            toast.success("Notification acknowledged — no approval action required");
            continue;
          }

          if (nextStatus === "Approved") {
            await service.approve(id, { comments: reason, remarks: reason });
            toast.success(
              kind === "confirmation"
                ? "Confirmation step approved"
                : kind === "transfer"
                  ? "Transfer step approved"
                  : "Separation step approved"
            );
          } else {
            await service.reject(id, { comments: reason, remarks: reason });
            toast.success(
              kind === "confirmation"
                ? "Confirmation rejected"
                : kind === "transfer"
                  ? "Transfer rejected"
                  : "Separation rejected"
            );
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Action failed");
        }
      }
      await refreshLifecycleDesks();
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        approvals: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setSelectedRequestId(null);
      window.dispatchEvent(new CustomEvent("admin:lifecycle-request-changed"));
    }

    if (helpdeskIds.length > 0 && (nextStatus === "Approved" || nextStatus === "Rejected")) {
      for (const requestId of helpdeskIds) {
        const request = findDeskRequest(allDeskRequests, requestId);
        const ticketId =
          request?.helpdeskTicketId || helpdeskTicketIdFromRequestId(requestId);
        if (!ticketId) continue;
        try {
          if (nextStatus === "Approved") {
            await helpdeskService.closeTicket(ticketId, {
              asResolved: true,
              resolutionNotes: reason,
            });
            toast.success("Ticket resolved");
          } else {
            await helpdeskService.closeTicket(ticketId, {
              asResolved: false,
              resolutionNotes: reason,
              rcaJson: { rejected: true, rejectionReason: reason },
            });
            toast.success("Ticket closed as rejected");
          }
        } catch (error) {
          toast.error(error.response?.data?.message || error.message || "Ticket action failed");
        }
      }
      await fetchHelpdeskDeskRequests();
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        approvals: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: nextStatus === "Approved" ? "Approved" : "Rejected",
      }));
      setSelectedRequestId(null);
    }

    const leaveIds = localIds.filter((id) => {
      const request = findDeskRequest(allDeskRequests, id);
      return isLeaveDeskRequest(request);
    });
    const flexiClaimIds = localIds.filter((id) => {
      const request = findDeskRequest(allDeskRequests, id);
      return isFlexiClaimDeskRequest(request) || flexiClaimIdFromDeskId(id) != null;
    });
    const otherLocalIds = localIds.filter(
      (id) => !leaveIds.includes(id) && !flexiClaimIds.includes(id)
    );

    if (
      leaveIds.length > 0 &&
      (nextStatus === "Approved" ||
        nextStatus === "Rejected" ||
        nextStatus === "Undo" ||
        nextStatus === "CancelRequested")
    ) {
      for (const requestId of leaveIds) {
        const numericId = leaveRequestIdFromDeskId(requestId);
        if (!numericId) continue;
        try {
          if (nextStatus === "Approved") {
            const res = await attendanceStructureService.approveLeaveRequest(
              numericId,
              reason
            );
            toast.success(
              res?.data?.status === "approved"
                ? "Leave request fully approved"
                : `Approved — pending at ${res?.data?.pendingAt || "next approver"}`
            );
          } else if (nextStatus === "Rejected") {
            await attendanceStructureService.rejectLeaveRequest(numericId, reason);
            toast.success("Leave request rejected");
          } else if (nextStatus === "CancelRequested") {
            const res = await attendanceStructureService.requestLeaveCancellation(
              numericId,
              reason
            );
            toast.success(
              res?.autoApproved
                ? "Leave cancelled"
                : "Leave cancellation request submitted"
            );
          } else {
            await attendanceStructureService.cancelLeaveRequest(numericId, reason);
            toast.success("Leave request undone");
          }
        } catch (error) {
          toast.error(
            error?.response?.data?.message || error.message || "Leave action failed"
          );
        }
      }
      await fetchLeaveDeskRequests();
      window.dispatchEvent(new CustomEvent("admin:leave-request-changed"));
      const deskStatus =
        nextStatus === "Approved"
          ? "Approved"
          : nextStatus === "Rejected"
            ? "Rejected"
            : nextStatus === "CancelRequested"
              ? "Pending"
              : "Undo";
      const panelKey = statusModal.panelKey || "review";
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        [panelKey]: deskStatus,
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: deskStatus,
      }));
      setSelectedRequestId(null);
    }

    if (
      flexiClaimIds.length > 0 &&
      (nextStatus === "Approved" ||
        nextStatus === "Rejected" ||
        nextStatus === "Query")
    ) {
      for (const requestId of flexiClaimIds) {
        const numericId =
          flexiClaimIdFromDeskId(requestId) ||
          findDeskRequest(allDeskRequests, requestId)?.flexiClaimId;
        if (!numericId) continue;
        try {
          const decision =
            nextStatus === "Approved"
              ? "APPROVED"
              : nextStatus === "Rejected"
                ? "REJECTED"
                : "CLARIFICATION";
          await flexiBenefitsService.decideClaim(numericId, {
            decision,
            remarks: reason,
          });
          toast.success(
            nextStatus === "Approved"
              ? "Reimbursement claim approved"
              : nextStatus === "Rejected"
                ? "Reimbursement claim rejected"
                : "Query raised on reimbursement claim"
          );
        } catch (error) {
          toast.error(
            error?.response?.data?.message ||
              error.message ||
              "Reimbursement claim action failed"
          );
        }
      }
      await fetchFlexiDeskRequests();
      window.dispatchEvent(new CustomEvent("admin:flexi-reimbursement-changed"));
      const deskStatus =
        nextStatus === "Approved"
          ? "Approved"
          : nextStatus === "Rejected"
            ? "Rejected"
            : "Pending";
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        approvals: deskStatus,
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: deskStatus,
      }));
      setSelectedRequestId(null);
    }

    if (otherLocalIds.length > 0 && nextStatus === "CancelRequested") {
      for (const requestId of otherLocalIds) {
        const request = findDeskRequest(allDeskRequests, requestId);
        if (!request || isDeskCancellationRequest(request)) continue;
        if (!isCancellableActionLabel(request.actionLabel)) continue;
        try {
          const { attendanceService } = await import("../../services/api");
          const id = request.serverDeskId || request.requestId;
          const res = await attendanceService.requestDeskCancellation(id, reason);
          toast.success(
            res?.autoApproved
              ? "Request cancelled"
              : "Cancellation request submitted"
          );
        } catch (error) {
          toast.error(
            error?.response?.data?.message ||
              error.message ||
              "Cancellation request failed"
          );
        }
      }
      try {
        const { hydrateCompOffOtDeskFromServer } = await import(
          "../../lib/compOffOtNotify"
        );
        const { loadQuickActionRequests } = await import(
          "../../lib/attendanceDeskRequests"
        );
        await hydrateCompOffOtDeskFromServer();
        const refreshed = loadQuickActionRequests() || [];
        setQuickActionRequests(
          refreshed.filter((item) => !isLeaveActionLabel(item?.actionLabel))
        );
      } catch (hydrateErr) {
        console.warn("Desk hydrate after cancellation request skipped:", hydrateErr);
      }
      const panelKey = statusModal.panelKey || "review";
      setSelectedStatusByPanel((prev) => ({
        ...prev,
        [panelKey]: "Pending",
      }));
      setStatusModal((prev) => ({
        ...prev,
        status: "Pending",
      }));
      setSelectedRequestId(null);
      return;
    }

    if (otherLocalIds.length === 1) {
      updateQuickRequestStatus(otherLocalIds[0], nextStatus, reason);
    } else if (otherLocalIds.length > 1) {
      updateQuickRequestStatusBulk(otherLocalIds, nextStatus, reason);
    }

    if (
      otherLocalIds.length > 0 &&
      (nextStatus === "Approved" || nextStatus === "Rejected" || nextStatus === "Undo")
    ) {
      try {
        const { loadQuickActionRequests } = await import(
          "../../lib/attendanceDeskRequests"
        );
        const { syncRestrictedHolidayDecisionAndNotify } = await import(
          "../../lib/restrictedHolidayNotify"
        );
        const {
          syncCompOffDecision,
          syncOvertimeDecision,
          syncRegulariseDecision,
          syncDeskRequestRecall,
          hydrateCompOffOtDeskFromServer,
        } = await import("../../lib/compOffOtNotify");
        const requests = loadQuickActionRequests() || [];
        let recallSynced = false;
        for (const rid of otherLocalIds) {
          const req = requests.find(
            (r) => String(r?.requestId) === String(rid)
          );
          const reqWithRemarks = { ...req, actionRemarks: reason };
          const label = String(req?.actionLabel || "");
          if (nextStatus === "Undo") {
            const ok = await syncDeskRequestRecall(
              reqWithRemarks,
              reason
            );
            if (ok) recallSynced = true;
            if (ok) {
              toast.success("Request recalled");
            } else if (label) {
              toast.error("Recall saved locally — server sync failed");
            }
            continue;
          }
          if (label === "Restricted Holiday") {
            await syncRestrictedHolidayDecisionAndNotify(reqWithRemarks, nextStatus);
          } else if (
            label === "Comp Off" ||
            label === "Comp Off Work" ||
            label === "Comp Off Credit"
          ) {
            await syncCompOffDecision(reqWithRemarks, nextStatus);
          } else if (label === "Overtime") {
            await syncOvertimeDecision(reqWithRemarks, nextStatus);
          } else if (label === "Regularise checks") {
            await syncRegulariseDecision(reqWithRemarks, nextStatus);
          } else if (
            (label === "Change WO" ||
              label === "Change Shift" ||
              label === "Short Leave" ||
              label === "On Duty" ||
              label === "Attendance Lock" ||
              label === "Attendance Unlock" ||
              label === "Attendance Finalization") &&
            (nextStatus === "Approved" || nextStatus === "Rejected")
          ) {
            try {
              const { attendanceService } = await import("../../services/api");
              const { getAuthJson } = await import("../../utils/authStorage");
              const id = reqWithRemarks?.serverDeskId || reqWithRemarks?.requestId;
              const currentUser = getAuthJson("currentUser", null);
              if (id) {
                const firstName = currentUser?.name || "";
                const lastName = currentUser?.lastName || "";
                const actorName = [firstName, lastName].filter(Boolean).join(" ") || null;
                await attendanceService.updateDeskQuickRequestStatus(id, nextStatus, {
                  reason: reason || null,
                  actorName,
                });
              }
            } catch (e) {
              console.warn(`${label} status sync skipped:`, e?.message || e);
            }
          }
        }
        if (nextStatus === "Undo" && recallSynced) {
          try {
            const { employeeId } = getQuickPanelUserIds();
            await hydrateCompOffOtDeskFromServer({ employeeId });
          } catch (hydrateErr) {
            console.warn("Desk hydrate after recall skipped:", hydrateErr?.message || hydrateErr);
          }
        }
        if (nextStatus === "Undo") {
          const panelKey = statusModal.panelKey || "review";
          setSelectedStatusByPanel((prev) => ({
            ...prev,
            [panelKey]: "Undo",
          }));
          setStatusModal((prev) => ({
            ...prev,
            status: "Undo",
          }));
          setSelectedRequestId(null);
        }
        // Refresh desk after Regularise → Comp Off Credit hydrate
        const refreshed = loadQuickActionRequests() || [];
        setQuickActionRequests(refreshed.filter((item) => !isLeaveActionLabel(item?.actionLabel)));
      } catch (err) {
        console.warn("RH status sync skipped:", err?.message || err);
      }
    }
    } finally {
      closeStatusActionModal();
    }
  };

  const resetQuickActionDraft = (actionLabel = "") => {
    const todayIso = getMaxDateInputValue();
    setQuickActionDraft({
      date: actionLabel === "Regularise checks" ? todayIso : "",
      proposedDate: "",
      fromDate: "",
      toDate: "",
      shift: "",
      currentShift: "",
      leaveType: "",
      leaveDuration: "full_day",
      inTime: "",
      outTime: "",
      startTime: "",
      endTime: "",
      resignationReason: "",
      resignationDate: "",
      lastWorkingDate: "",
      reason: "",
      notes: "",
      ticketType: "",
      ticketCategory: "",
      ticketSubcategory: "",
      ticketPriority: "medium",
      ticketSubject: "",
      amount: "",
      investmentSection: "",
      financialYear: "",
      taxRegime: "new",
      positionTitle: "",
      positionsCount: "1",
      requiredByDate: "",
      requestDepartment: "",
      requestDesignation: "",
    });
  };

  const buildQuickActionContext = (actionLabel) => {
    const currentUser = getAuthJson("currentUser");
    const rawEmployeeId =
      getAuthItem("employeeId") ||
      currentUser?.employeeId ||
      currentUser?.employee?.id ||
      null;
    const numericId = rawEmployeeId != null && rawEmployeeId !== ""
      ? Number(rawEmployeeId)
      : null;
    const rawEmployeeCode =
      currentUser?.empCode ||
      currentUser?.employeeCode ||
      currentUser?.employee?.empCode ||
      currentUser?.employee?.employeeCode ||
      currentUser?.employee?.emp_code ||
      currentUser?.employee?.employee_code ||
      "";
    const employeeCode = String(rawEmployeeCode || "").trim();
    return {
      actionLabel,
      employeeId: Number.isFinite(numericId) ? String(numericId) : null,
      employeeCode: employeeCode || null,
      userId: currentUser?.id || null,
      userName: currentUser?.name || "",
      userEmail: currentUser?.email || currentUser?.username || "",
      gender:
        currentUser?.gender ||
        currentUser?.employee?.gender ||
        currentUser?.user?.gender ||
        "",
      requestedAt: Date.now(),
    };
  };

  const openStatusDeskForm = () => {
    const selectedType = selectedTypeByPanel[statusModal.panelKey] || "";
    if (!selectedType || selectedType === "Announcements") return;

    if (selectedType === RESIGNATION_REQUEST_LABEL) {
      const context = buildQuickActionContext(selectedType);
      if (!context.employeeId) {
        toast.error(
          "Your employee profile is required to resign. Link your user to an employee record, or use Employee → Resignation."
        );
        return;
      }
      setStatusDeskContext(context);
      setResignationFormOpen(true);
      setSelectedRequestId(null);
      return;
    }

    const lifecycleCreateBlocked = new Set([
      CONFIRMATION_REQUEST_LABEL,
      TRANSFER_REQUEST_LABEL,
      ONBOARDING_REQUEST_LABEL,
      "Employee Onboarding Approval",
      "Onboarding Task",
      "Confirmation Approval",
      "Transfer Approval",
      "Exit / Separation Approval",
      "Resignation Approval",
      "Termination Approval",
      "Termination / Separation Approval",
    ]);
    if (lifecycleCreateBlocked.has(selectedType)) {
      toast(
        "Raise this request from Employee module. Use Request here to track status, and Approvals to act on pending steps.",
        { icon: "ℹ️" }
      );
      navigate("/admin/employee");
      return;
    }

    if (isPayrollReimbursementDeskLabel(selectedType)) {
      const mode = flexiReimbursementDeskMode(selectedType);
      const context = buildQuickActionContext(selectedType);
      if (!context.employeeId) {
        toast.error(
          "Your employee profile is required for reimbursement requests. Link your user to an employee record, or use Employee → Payroll → Reimbursement."
        );
        return;
      }
      setStatusDeskContext(context);
      setFlexiReimbModal({ open: true, mode: mode || "declare" });
      setSelectedRequestId(null);
      return;
    }

    const context = buildQuickActionContext(selectedType);
    resetQuickActionDraft(selectedType);
    setStatusDeskContext(context);
    setStatusDeskShowForm(true);
    setSelectedRequestId(null);
  };

  const closeStatusDeskForm = () => {
    setStatusDeskShowForm(false);
    setQuickActionSubmitting(false);
    quickActionSubmitLockRef.current = false;
  };

  const submitRegulariseQuickAction = async (formData) => {
    const context = statusDeskContext;
    if (!context?.employeeId) {
      toast.error("Your employee profile is required to submit regularisation.");
      return;
    }
    try {
      const entry = await submitRegulariseRequest({
        employeeId: context.employeeId,
        userName: context.userName || "Current user",
        userId: context.userId || null,
        userEmail: context.userEmail || "",
        formData,
        mode: "ess",
      });
      setQuickActionRequests((prev) => [entry, ...prev.filter((r) => r.requestId !== entry.requestId)]);
      closeStatusDeskForm();
      setSelectedRequestId(entry.requestId);
      toast.success("Regularisation request submitted");
    } catch (err) {
      toast.error(err?.message || "Failed to submit regularisation");
    }
  };

  const submitTaxPlanningQuickAction = (formData) => {
    const context = statusDeskContext;
    const needsApproval = !!formData?._needsApproval;
    const { _needsApproval, ...deskFormData } = formData || {};
    const payload = {
      ...context,
      formData: deskFormData,
      submittedAt: Date.now(),
      source: "sidebar_status_desk",
    };
    try {
      localStorage.setItem("adminQuickActionDraft", JSON.stringify(payload));
    } catch {
      /* ignore */
    }
    // No Declaration workflow configured → auto-approved (do not leave Pending).
    const status = needsApproval ? "Pending" : "Approved";
    const requestEntry = {
      requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      panelKey: "review",
      actionLabel: "Tax Planning",
      employeeId: context?.employeeId || null,
      userId: context?.userId || null,
      userName: context?.userName || "Current user",
      userEmail: context?.userEmail || "",
      status,
      submittedAt: Date.now(),
      formData: deskFormData,
      pendingWith: needsApproval ? "Payroll Team" : null,
      workflowTrail: [
        {
          status: needsApproval ? "Pending" : "Approved",
          actor: context?.userName || "Current user",
          actedAt: Date.now(),
          note: needsApproval
            ? "Awaiting declaration approval workflow"
            : "Auto-approved (no declaration workflow configured)",
        },
      ],
    };
    setQuickActionRequests((prev) => {
      const next = [requestEntry, ...prev];
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    window.dispatchEvent(
      new CustomEvent("admin:quick-action-submitted", {
        detail: {
          ...payload,
          requestId: requestEntry.requestId,
          actionLabel: requestEntry.actionLabel,
          status,
        },
      })
    );
    setSelectedRequestId(requestEntry.requestId);
    if (!needsApproval) {
      toast.success("Tax Planning submitted and auto-approved (no declaration workflow)");
    }
  };

  const submitQuickAction = async (e) => {
    e.preventDefault();
    if (quickActionSubmitLockRef.current || quickActionSubmitting) return;
    quickActionSubmitLockRef.current = true;
    setQuickActionSubmitting(true);

    try {
    const context = statusDeskContext;
    const actionLabel =
      context?.actionLabel === "Tax Declaration" || context?.actionLabel === "Tax Planning"
        ? "Tax Planning"
        : context?.actionLabel;
    const payload = {
      ...context,
      formData: quickActionDraft,
      submittedAt: Date.now(),
      source: "sidebar_status_desk",
    };
    try {
      localStorage.setItem("adminQuickActionDraft", JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }

    if (
      actionLabel === "Change Shift" ||
      actionLabel === "Change WO" ||
      actionLabel === "Short Leave" ||
      actionLabel === "Regularise checks" ||
      actionLabel === "On Duty" ||
      actionLabel === "Comp Off" ||
      actionLabel === "Overtime" ||
      actionLabel === "Restricted Holiday"
    ) {
      if (!context?.employeeId) {
        toast.error(
          "Your employee profile is required to submit this request. Use Access ESS or log in as an employee."
        );
        return;
      }
      try {
        // Request / Approvals desk always raises ESS workflow (Pending).
        // Admin Register/Calendar uses admin_direct elsewhere and auto-applies.
        const mode =
          context?.source === "admin_direct" ? "admin_direct" : "ess";
        const common = {
          employeeId: context.employeeId,
          employeeCode: context.employeeCode || null,
          userName: context.userName || "Current user",
          userId: context.userId || null,
          userEmail: context.userEmail || "",
          formData: quickActionDraft,
          mode,
        };
        let entry;
        if (actionLabel === "Change Shift") {
          if (!String(quickActionDraft?.shift || "").trim()) {
            toast.error("Select a new shift.");
            return;
          }
          entry = await submitChangeShiftRequest(common);
        } else if (actionLabel === "Change WO") {
          if (!String(quickActionDraft?.date || "").trim()) {
            toast.error("Select the current Weekly Off date.");
            return;
          }
          if (!String(quickActionDraft?.proposedDate || "").trim()) {
            toast.error("Select the proposed Weekly Off date.");
            return;
          }
          if (!String(quickActionDraft?.reason || "").trim()) {
            toast.error("Enter a reason.");
            return;
          }
          entry = await submitChangeWoRequest(common);
        } else if (actionLabel === "Short Leave") {
          entry = await submitShortLeaveRequest(common);
        } else if (actionLabel === "Regularise checks") {
          entry = await submitRegulariseRequest(common);
        } else if (actionLabel === "On Duty") {
          if (!String(quickActionDraft?.date || "").trim()) {
            toast.error("Select a date.");
            return;
          }
          entry = await submitAttendanceActionRequest({
            ...common,
            actionLabel: "On Duty",
          });
        } else if (actionLabel === "Comp Off") {
          if (!String(quickActionDraft?.date || "").trim()) {
            toast.error("Select a date.");
            return;
          }
          const { submitCompOffRequest } = await import(
            "../../lib/attendanceDeskRequests"
          );
          entry = await submitCompOffRequest(common);
        } else if (actionLabel === "Restricted Holiday") {
          if (
            !String(quickActionDraft?.holidayName || "").trim() &&
            quickActionDraft?.holidayId == null
          ) {
            toast.error("Select a Restricted Holiday from the list.");
            return;
          }
          if (!String(quickActionDraft?.date || "").trim()) {
            toast.error("Select a Restricted Holiday from the list.");
            return;
          }
          const { submitRestrictedHolidayRequest } = await import(
            "../../lib/attendanceDeskRequests"
          );
          entry = await submitRestrictedHolidayRequest(common);
        } else {
          if (!String(quickActionDraft?.date || "").trim()) {
            toast.error("Select a date.");
            return;
          }
          if (
            !String(quickActionDraft?.startTime || "").trim() ||
            !String(quickActionDraft?.endTime || "").trim()
          ) {
            toast.error("Enter start and end time.");
            return;
          }
          const { submitOvertimeRequest } = await import(
            "../../lib/attendanceDeskRequests"
          );
          entry = await submitOvertimeRequest(common);
        }
        setQuickActionRequests((prev) => [
          entry,
          ...prev.filter((r) => r.requestId !== entry.requestId),
        ]);
        window.dispatchEvent(
          new CustomEvent("admin:quick-action-submitted", {
            detail: {
              ...payload,
              requestId: entry.requestId,
              actionLabel: entry.actionLabel,
            },
          })
        );
        closeStatusDeskForm();
        setSelectedRequestId(entry.requestId);
        toast.success(
          entry.status === "Approved"
            ? "Request applied (direct approval)"
            : `Request submitted — Pending with ${entry.pendingWith || "Reporting Manager"}`
        );
        return;
      } catch (err) {
        toast.error(err?.message || "Failed to submit request");
        return;
      }
    }

    let requestEntry = {
      requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      panelKey: "review",
      actionLabel: context?.actionLabel || actionLabel,
      employeeId: context?.employeeId || null,
      userId: context?.userId || null,
      userName: context?.userName || "Current user",
      userEmail: context?.userEmail || "",
      status: "Pending",
      submittedAt: Date.now(),
      formData: quickActionDraft,
      pendingWith: "Reporting Manager",
      workflowTrail: [
        {
          status: "Pending",
          actor: context?.userName || "Current user",
          actedAt: Date.now(),
        },
      ],
    };

    if (actionLabel === "Leave") {
      if (!context?.employeeId) {
        toast.error(
          "Your employee profile is required to submit leave. Use Access ESS or log in as an employee."
        );
        return;
      }
      try {
        const fd = quickActionDraft || {};
        const apiRes = await attendanceStructureService.createLeaveRequest({
          employeeId: Number(context.employeeId),
          leaveStatusCode: fd.leaveType,
          leaveDuration: fd.leaveDuration || "full_day",
          fromDate: fd.fromDate,
          toDate: fd.toDate || fd.fromDate,
          reason: fd.reason,
          notes: fd.notes,
        });
        if (apiRes?.data) {
          const mapped = mapLeaveRequestApiToInsight(apiRes.data);
          await fetchLeaveDeskRequests();
          window.dispatchEvent(new CustomEvent("admin:leave-request-changed"));
          window.dispatchEvent(
            new CustomEvent("admin:quick-action-submitted", {
              detail: {
                ...payload,
                requestId: mapped?.requestId,
                actionLabel: "Leave",
              },
            })
          );
          if (apiRes.autoApproved) {
            toast.success("Leave auto-approved and applied");
          } else {
            toast.success(`Leave submitted — pending at ${mapped?.pendingAt || "approver"}`);
          }
          closeStatusDeskForm();
          setSelectedRequestId(mapped?.requestId ?? null);
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to submit leave request");
        return;
      }
    }

    if (actionLabel === "Ticket" || actionLabel === TICKET_ACTION_LABEL) {
      const fd = quickActionDraft || {};
      if (!fd.ticketType) {
        toast.error("Select a ticket type");
        return;
      }
      if (!fd.ticketCategory) {
        toast.error("Select a category");
        return;
      }
      if (!String(fd.ticketSubject || "").trim()) {
        toast.error("Subject is required");
        return;
      }
      try {
        const apiRes = await helpdeskService.createTicket({
          title: String(fd.ticketSubject).trim(),
          description: String(fd.reason || fd.notes || "").trim(),
          typeId: fd.ticketType,
          categoryId: fd.ticketCategory || null,
          subcategoryId: fd.ticketSubcategory || null,
          priorityId: fd.ticketPriority || "medium",
          employeeId: context?.employeeId || null,
          source: "request_desk",
        });
        if (apiRes?.data) {
          const mapped = buildHelpdeskDeskItems({
            mine: [apiRes.data],
            pendingMyAction: [],
          })[0];
          requestEntry = {
            ...mapped,
            panelKey: "review",
          };
          setHelpdeskDeskRequests((prev) => {
            const without = prev.filter(
              (r) => String(r.helpdeskTicketId) !== String(apiRes.data.id)
            );
            return [mapped, ...without];
          });
          window.dispatchEvent(new CustomEvent("admin:helpdesk-ticket-changed"));
          toast.success(
            `${apiRes.data.ticketNo} raised — pending with ${
              apiRes.data.pendingWith || "Helpdesk Queue"
            }`
          );
          closeStatusDeskForm();
          setSelectedRequestId(mapped.requestId);
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to submit ticket");
        return;
      }
    }

    setQuickActionRequests((prev) => {
      const next = [requestEntry, ...prev];
      try {
        localStorage.setItem("adminQuickActionRequests", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
    window.dispatchEvent(
      new CustomEvent("admin:quick-action-submitted", {
        detail: { ...payload, requestId: requestEntry.requestId, actionLabel: requestEntry.actionLabel },
      })
    );
    closeStatusDeskForm();
    setSelectedRequestId(requestEntry.requestId);
    toast.success("Request submitted");
    } finally {
      quickActionSubmitLockRef.current = false;
      setQuickActionSubmitting(false);
    }
  };

  const handleUsefulLinkClick = (link) => {
    if (!link?.href) return;
    if (link.key === "epf_establishment") {
      const pfCode =
        companyData?.pfEstablishmentCode ||
        companyData?.pf_code ||
        companyData?.pfCode ||
        "";
      const pfUsername = pfCode;
      const info = [
        "Opening EPF Establishment Portal.",
        pfCode
          ? `PF Establishment Code: ${pfCode}`
          : "PF Establishment Code: Not available",
        pfUsername
          ? `Username (auto): ${pfUsername}`
          : "Username: Not available",
      ].join("\n");
      if (pfUsername && navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(pfUsername).catch(() => {});
      }
      alert(info);
    }
    window.open(link.href, "_blank", "noopener,noreferrer");
  };

  const panelTypeItemsByKey = useMemo(() => {
    const allowQuickPanel = (screenKey) =>
      allowEssQuickPanelScreen(hasScreenAccess, isAdmin, screenKey);

    const currentUser = getAuthJson("currentUser", null);
    const { employeeId: selfEmpId } = getQuickPanelUserIds();

    // Richer stub so "all employees" / org applicability matches the signed-in person.
    const selfAccess = resolveEmployeeAttendanceRequestAccess(
      employeeStubFromIds({
        employeeId: selfEmpId,
        empCode:
          currentUser?.empCode ||
          currentUser?.employeeCode ||
          currentUser?.employee?.empCode ||
          currentUser?.employee?.employeeCode ||
          null,
        categoryId:
          currentUser?.categoryId ||
          currentUser?.employee?.categoryId ||
          currentUser?.category?.id ||
          null,
        departmentId:
          currentUser?.departmentId ||
          currentUser?.employee?.departmentId ||
          currentUser?.department?.id ||
          null,
        branchId:
          currentUser?.branchId ||
          currentUser?.employee?.branchId ||
          currentUser?.branch?.id ||
          null,
        employmentType:
          currentUser?.employmentType ||
          currentUser?.employee?.employmentType ||
          null,
        employmentStatus:
          currentUser?.employmentStatus ||
          currentUser?.employee?.employmentStatus ||
          null,
      })
    );

    // My requests: Quick Panel view ∧ policy (attendance types). No linked-employee bypass.
    const requestTypeAllowed = (item) => {
      if (item.key === "resignation") {
        return (
          canRaiseEssRequestType({
            key: "resignation",
            hasScreenAccess,
            isAdmin,
            policyAccess: selfAccess,
          }) || allowQuickPanel("exit_separation_approval")
        );
      }
      return canRaiseEssRequestType({
        key: item.key,
        hasScreenAccess,
        isAdmin,
        policyAccess: selfAccess,
      });
    };

    const requestTypes = REQUEST_TYPE_DEFS.filter(requestTypeAllowed)
      .filter((item) => item.key !== "comp_off")
      .map((item) => item.label);
    // Lifecycle my-requests are raised from Employee module; always list them so Request
    // desk stays open for tracking (incl. onboarding drafts) even when ESS raise RBAC is empty.
    const lifecycleRequestTypes = LIFECYCLE_REQUEST_TYPE_DEFS.map((item) => item.label);
    // Approval-only lifecycle types are assignee-driven (API filters who can act).
    // Always list them so Approvals stays open even when raise-RBAC screens are empty.
    const approvalOnly = APPROVAL_ONLY_TYPE_DEFS.map((item) => item.label);
    // Approvals queue: RBAC for non-attendance; attendance types listed when Quick Panel allows
    // so RMs can filter (policy not required for viewing others' approvals).
    const approvalRequestTypes = Array.from(
      new Set([
        ...REQUEST_TYPE_DEFS.filter((item) => {
          if (item.key === "resignation") {
            return (
              allowQuickPanel(item.key) ||
              allowQuickPanel("exit_separation_approval") ||
              allowQuickPanel("resignation")
            );
          }
          return allowQuickPanel(item.key);
        }).map((item) => item.label),
        // Always list RM-routed attendance types so RMs can filter Approvals
        // even without the raise-side Quick Panel chip.
        ...REQUEST_TYPE_DEFS.filter((item) => isRmRoutedActionLabel(item.label)).map(
          (item) => item.label
        ),
        "Comp Off Credit",
      ])
    );
    const reviewTypes = Array.from(
      new Set([...requestTypes, ...lifecycleRequestTypes, ...CANCELLATION_ACTION_LABELS])
    );
    return {
      review: reviewTypes,
      approvals: [...approvalRequestTypes, ...approvalOnly, ...CANCELLATION_ACTION_LABELS],
    };
    // companyData: Tax Planning follows the Payroll module flag
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScreenAccess, isAdmin, snapshot, woAccessTick, companyData]);

  const typeBadgeCountsByPanel = useMemo(() => {
    const buildCounts = (panelKey, typeLabels) => {
      const counts = {
        [ALL_DESK_TYPES]: getRequestsForStatus(panelKey, "Pending", ALL_DESK_TYPES).length,
      };
      (typeLabels || []).forEach((label) => {
        counts[label] = getRequestsForStatus(panelKey, "Pending", label).length;
      });
      return counts;
    };
    return {
      review: buildCounts("review", panelTypeItemsByKey.review),
      approvals: buildCounts("approvals", panelTypeItemsByKey.approvals),
    };
  }, [getRequestsForStatus, panelTypeItemsByKey]);

  const sortedTypesByPanel = useMemo(() => {
    const buildSorted = (panelKey, typeLabels) => {
      const counts = typeBadgeCountsByPanel[panelKey] || {};
      const sortedRest = sortDeskTypesByBadgeDesc(typeLabels || [], counts);
      return [ALL_DESK_TYPES, ...sortedRest];
    };
    return {
      review: buildSorted("review", panelTypeItemsByKey.review),
      approvals: buildSorted("approvals", panelTypeItemsByKey.approvals),
    };
  }, [panelTypeItemsByKey, typeBadgeCountsByPanel]);

  const groupedTypesByPanel = useMemo(() => {
    const buildGroups = (panelKey, typeLabels, sortByBadgeDesc) => {
      const counts = typeBadgeCountsByPanel[panelKey] || {};
      return groupDeskTypesByModule(typeLabels, counts, { sortByBadgeDesc });
    };
    return {
      review: buildGroups("review", panelTypeItemsByKey.review, false),
      approvals: buildGroups("approvals", panelTypeItemsByKey.approvals, true),
    };
  }, [panelTypeItemsByKey, typeBadgeCountsByPanel]);

  const rightItems = useMemo(
    () => [
      {
        key: "review",
        label: "Request",
        icon: ClipboardList,
      },
      {
        key: "approvals",
        label: "Approvals",
        icon: BadgeCheck,
      },
    ],
    []
  );

  const usefulLinks = useMemo(
    () => ({
      employee: [
        { key: "pf_member", label: "PF Member Portal", href: "https://unifiedportal-mem.epfindia.gov.in/memberinterface/" },
        { key: "esi_member", label: "ESI Member Portal", href: "https://www.esic.in/EmployeePortal/login.aspx" },
        { key: "pf_passbook", label: "PF Passbook", href: "https://passbook.epfindia.gov.in/MemberPassBook/Login" },
        { key: "it_login", label: "Income Tax Login", href: "https://www.incometax.gov.in/iec/foportal/" },
      ],
      employer: [
        { key: "epf_establishment", label: "EPF Establishment Login", href: "https://unifiedportal-emp.epfindia.gov.in/epfo/" },
        { key: "esi_establishment", label: "ESI Establishment Login", href: "https://portal.esic.gov.in/EmployerPortal/ESICInsurancePortal/Portal_Loginnew.aspx" },
        { key: "pt_login", label: "PT Login", href: "https://pt.kar.nic.in/" },
        { key: "ekarmika", label: "eKarmika Login", href: "https://www.ekarmika.karnataka.gov.in/ekarmika/static/home.aspx" },
        { key: "lwf_login", label: "LWF Login", href: "https://klwbapps.karnataka.gov.in/lowf/" },
      ],
    }),
    []
  );

  const leftItems = useMemo(
    () => [
      {
        key: "home",
        label: "Home",
        icon: Home,
        to: "/admin/home",
      },
    ],
    []
  );

  const employeeSectionItems = useMemo(
    () => [
      {
        key: "profile",
        label: "My profile",
        icon: UserRound,
        to: "/employee/profile",
      },
      {
        key: "attendance",
        label: "Attendance logs",
        icon: CalendarClock,
        to: "/employee/attendance",
      },
      {
        key: "payroll",
        label: "Personal payroll",
        icon: Wallet,
        to: "/employee/payroll",
      },
    ],
    []
  );

  const teamItems = useMemo(
    () =>
      showMyTeam
        ? [
            {
              key: "my_team",
              label: "My team",
              icon: UsersRound,
              to: "/employee/my-team",
            },
          ]
        : [],
    [showMyTeam]
  );

  const deskStatusCounts = useMemo(() => {
    if (!statusModal.open) return {};
    const panelKey = statusModal.panelKey;
    const typeLabel = selectedTypeByPanel[panelKey] || ALL_DESK_TYPES;
    return {
      Pending: getRequestsForStatus(panelKey, "Pending", typeLabel).length,
      Approved: getRequestsForStatus(panelKey, "Approved", typeLabel).length,
      Rejected: getRequestsForStatus(panelKey, "Rejected", typeLabel).length,
      Undo: getRequestsForStatus(panelKey, "Undo", typeLabel).length,
    };
  }, [
    statusModal.open,
    statusModal.panelKey,
    selectedTypeByPanel,
    getRequestsForStatus,
  ]);

  const approvalsPendingBadge = useMemo(() => {
    const count = getRequestsForStatus("approvals", "Pending", ALL_DESK_TYPES)
      .length;
    return count > 0 ? count : null;
  }, [getRequestsForStatus]);

  const deskItems = useMemo(() => {
    // Always keep Request / Approvals visible — inbox rows come from APIs even when
    // ESS raise-type chips are still loading or empty.
    return rightItems || [];
  }, [rightItems]);

  // Do not bounce /admin/requests → Home when type chips are empty (race / RBAC).
  // Approvals already stayed open for the same reason.

  const canOpenActivityConsole =
    isAdmin || hasScreenAccess("configuration", "activity_console", "view");

  const isActivityConsoleOpen = location.pathname.startsWith(
    "/admin/activity-console"
  );

  const handleActivityConsoleToggle = useCallback(
    (isOpen) => {
      if (isOpen) {
        const ret = consumeActivityConsoleReturn();
        if (ret?.pathname) {
          navigate(`${ret.pathname}${ret.search || ""}`);
          if (ret.page) setCurrentPage(ret.page);
        } else {
          navigate(-1);
        }
        return;
      }
      rememberActivityConsoleReturn(location);
      navigate("/admin/activity-console?module=all");
      setCurrentPage("Activity Console");
    },
    [location, navigate, setCurrentPage]
  );

  const adminNavVisible = useMemo(
    () =>
      navItems
        .filter((item) => getModuleStatus(item.module).isEnabled)
        .filter((item) => isAdmin || hasAnyModuleAccess(item.rbacModule))
        .map((item) => {
          if (!Array.isArray(item.children) || item.children.length === 0) {
            return item;
          }
          const can = (screen, action = "view") =>
            hasScreenAccess(item.rbacModule, screen, action);
          const children = item.children.filter((child) => {
            if (child.key === "activity-console" || child.key === "insights") {
              return canOpenActivityConsole;
            }
            const gate = NAV_CHILD_GATES[item.rbacModule]?.[child.key];
            return isAdmin || !gate || gate(can);
          });
          return { ...item, children };
        }),
    // getModuleStatus closes over companyData
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAnyModuleAccess, hasScreenAccess, isAdmin, snapshot, canOpenActivityConsole, companyData]
  );

  const workspaceNavVisible = useMemo(() => {
    return WORKSPACE_DEFS.filter((ws) => {
      if (ws.key === "reports" && !getModuleStatus("reports").isEnabled) return false;
      if (isAdmin) return true;
      const features = getFeaturesForWorkspace(ws.key);
      return features.some((f) =>
        hasScreenAccess(f.rbacModule, f.rbacScreen, f.rbacAction || "view")
      );
    }).map((ws) => ({
      ...ws,
      icon: WORKSPACE_NAV_ICONS[ws.key] || FileBarChart,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScreenAccess, isAdmin, snapshot, companyData]);

  /** Admin secondary bar when user has admin/workspace access. Both rails stay visible — no ESS/Admin flip. */
  const showAdminChrome = useMemo(
    () =>
      !!isAdmin ||
      adminNavVisible.length > 0 ||
      workspaceNavVisible.length > 0,
    [isAdmin, adminNavVisible.length, workspaceNavVisible.length]
  );

  // Keep shellMode aligned with access (drives secondary bar height / aside merge).
  useEffect(() => {
    if (showAdminChrome) {
      if (shellMode !== "admin") setShellMode("admin");
    } else if (shellMode === "admin") {
      setShellMode("employee");
    }
  }, [showAdminChrome, shellMode]);

  const navigateAdminChild = useCallback(
    (item, child) => {
      setShellMode("admin");
      setExpandedDeskKey(null);
      // Close secondary flyout after choosing a section
      setExpandedAdminKey(null);
      if (child.key === "activity-console" || child.key === "insights") {
        const moduleKey =
          item.rbacModule === "employee" ||
          item.rbacModule === "attendance" ||
          item.rbacModule === "payroll"
            ? item.rbacModule
            : "all";
        rememberActivityConsoleReturn(location);
        navigate(`/admin/activity-console?module=${encodeURIComponent(moduleKey)}`);
        setCurrentPage("Activity Console");
        return;
      }
      if (child.key === "attendanceCodes") {
        navigate("/admin/attendance?tab=settings");
        setCurrentPage("Attendance");
        try {
          window.dispatchEvent(
            new CustomEvent(ADMIN_SHELL_NAV_EVENT, {
              detail: {
                module: "attendance",
                path: "/admin/attendance",
                tab: "settings",
                structureTab: "codesAndDevices",
              },
            })
          );
        } catch {
          /* ignore */
        }
        return;
      }
      const to = `${item.to}?tab=${encodeURIComponent(child.key)}`;
      navigate(to);
      setCurrentPage(item.name);
      try {
        window.dispatchEvent(
          new CustomEvent(ADMIN_SHELL_NAV_EVENT, {
            detail: {
              module: item.rbacModule,
              path: item.to,
              tab: child.key,
            },
          })
        );
      } catch {
        /* ignore */
      }
    },
    [navigate, setCurrentPage, location]
  );

  const toggleAdminAccordion = useCallback(
    (item) => {
      const { isEnabled } = getModuleStatus(item.module);
      if (!isEnabled) {
        alert(`The ${item.name} module is not enabled for your company plan.`);
        return;
      }
      setShellMode("admin");
      setExpandedDeskKey(null);
      // Open/close flyout only — do not auto-navigate to the first sub-item.
      setExpandedAdminKey((prev) => (prev === item.name ? null : item.name));
    },
    // getModuleStatus closes over companyData; re-bind when that changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyData]
  );

  const handleSecondaryModuleClick = useCallback(
    (item) => {
      if (item == null) {
        setExpandedAdminKey(null);
        setSecondaryFlipPersistent(null);
        return;
      }
      const { isEnabled } = getModuleStatus(item.module);
      if (!isEnabled) {
        alert(`The ${item.name} module is not enabled for your company plan.`);
        return;
      }
      // Always show the name face for the clicked tile (do not toggle off).
      setSecondaryFlipPersistent(item.name);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      if (hasChildren) {
        toggleAdminAccordion(item);
        return;
      }
      setExpandedAdminKey(null);
      setExpandedDeskKey(null);
      if (item.name === "Employee" && location.pathname === item.to) {
        window.dispatchEvent(new CustomEvent("closeEmployeeForm"));
      }
      navigate(item.to);
      setCurrentPage(
        item.name === "Attendance" ? "Time & Attendance" : item.name
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyData, navigate, setCurrentPage, toggleAdminAccordion, setSecondaryFlipPersistent]
  );

  const handleSecondaryWorkspaceClick = useCallback(
    (ws) => {
      if (ws.key === "reports") {
        const { isEnabled } = getModuleStatus("reports");
        if (!isEnabled) {
          alert(`The Reports module is not enabled for your company plan.`);
          return;
        }
      }
      setExpandedAdminKey(null);
      setExpandedDeskKey(null);
      setSecondaryFlipPersistent(`ws:${ws.key}`);
      navigate(ws.path);
      setCurrentPage(ws.label);
    },
    [navigate, setCurrentPage, setSecondaryFlipPersistent, companyData]
  );

  const handleSecondaryChildClick = useCallback(
    (item, child) => {
      navigateAdminChild(item, child);
      setSecondaryFlipPersistent(item.name);
    },
    [navigateAdminChild, setSecondaryFlipPersistent]
  );

  const openRailFlip = useCallback(
    (key) => {
      setRailFlipPersistent(key);
    },
    [setRailFlipPersistent]
  );

  const activeTabKey = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get("tab") || "";
    } catch {
      return "";
    }
  }, [location.search]);

  return (
    <>
      {shellMode === "admin" ? (
        <AdminSecondaryNav
          modules={adminNavVisible}
          workspaceItems={workspaceNavVisible}
          expandedKey={expandedAdminKey}
          flippedKey={flippedSecondaryKey}
          activeTabKey={activeTabKey}
          pathname={location.pathname}
          onModuleClick={handleSecondaryModuleClick}
          onChildClick={handleSecondaryChildClick}
          onWorkspaceClick={handleSecondaryWorkspaceClick}
          getModuleEnabled={(item) => getModuleStatus(item.module).isEnabled}
          showActivityConsole={canOpenActivityConsole}
          activityConsoleOpen={isActivityConsoleOpen}
          onActivityConsoleToggle={handleActivityConsoleToggle}
        />
      ) : null}

      <div
        className="admin-shell-rail pace-shell-rail fixed left-0 z-50"
        style={{
          width: LEFT_SIDEBAR_WIDTH,
          top: "5rem",
          height: "calc(100dvh - 5rem)",
        }}
      >
        <aside
          className="admin-shell-rail pace-shell-rail flex h-full flex-col overflow-hidden"
          style={{ width: LEFT_SIDEBAR_WIDTH }}
        >
          <div className="admin-shell-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <ul className="pace-shell-rail-list">
              {leftItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <li key={item.key} className="pace-shell-rail-item">
                    <ShellFlipTile
                      variant="line"
                      icon={item.icon}
                      label={item.label}
                      title={item.label}
                      axis="y"
                      active={isActive}
                      flipped={flippedRailKey === item.key}
                      onClick={() => {
                        openRailFlip(item.key);
                        navigate(item.to);
                        setCurrentPage(item.label);
                      }}
                    />
                  </li>
                );
              })}

              {deskItems.map((item) => {
                const routeActive =
                  (item.key === "review" && location.pathname === DESK_ROUTES.review) ||
                  (item.key === "approvals" && location.pathname === DESK_ROUTES.approvals);
                return (
                  <li key={item.key} className="pace-shell-rail-item">
                    <ShellFlipTile
                      variant="line"
                      icon={item.icon}
                      label={item.label}
                      title={item.label}
                      axis="y"
                      active={routeActive}
                      flipped={flippedRailKey === item.key}
                      badge={
                        item.key === "approvals" ? approvalsPendingBadge : null
                      }
                      onClick={() => {
                        openRailFlip(item.key);
                        toggleDeskAccordion(item.key);
                      }}
                    />
                  </li>
                );
              })}

              {[...employeeSectionItems, ...teamItems].map((item) => {
                    const isActive = location.pathname === item.to;
                    return (
                      <li key={item.key} className="pace-shell-rail-item">
                        <ShellFlipTile
                          variant="line"
                          icon={item.icon}
                          label={item.label}
                          title={item.label}
                          axis="y"
                          active={isActive}
                          flipped={flippedRailKey === item.key}
                          onClick={() => {
                            openRailFlip(item.key);
                            navigate(item.to);
                            setCurrentPage(item.label);
                          }}
                        />
                      </li>
                    );
                  })}
            </ul>
          </div>

          {/* Useful Links — icon only on narrow rail */}
          <div className="relative mt-auto">
            {expandedPanels.usefulLinks && (
              <div className="absolute bottom-full left-0 z-[60] mb-1 w-56 max-h-64 overflow-y-auto rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2 shadow-[var(--ds-shadow-panel)]">
                <div className="mb-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      setUsefulLinksAudience((a) =>
                        a === "employee" ? "employer" : "employee"
                      )
                    }
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      usefulLinksAudience === "employee"
                        ? "border-[hsl(var(--ds-secondary))] bg-[hsl(var(--ds-secondary))] text-[hsl(var(--ds-secondary-fg))]"
                        : "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                    }`}
                    title="Toggle Employee / Employer links"
                  >
                    {usefulLinksAudience === "employee" ? "ESS" : "ER"}
                  </button>
                  <span className="text-[10px] font-semibold text-[hsl(var(--ds-muted-fg))]">
                    Useful links
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(usefulLinksAudience === "employee"
                    ? usefulLinks.employee
                    : usefulLinks.employer
                  ).map((link) => (
                    <button
                      key={link.key}
                      type="button"
                      onClick={() => handleUsefulLinkClick(link)}
                      className="rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-2 py-1 text-[10px] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              className={`pace-shell-useful-btn ${
                expandedPanels.usefulLinks ? "is-open" : ""
              }`}
              title="Useful links"
              aria-label="Useful links"
              onClick={() =>
                setExpandedPanels((prev) => ({
                  ...prev,
                  usefulLinks: !prev.usefulLinks,
                }))
              }
            >
              <ExternalLink size={22} strokeWidth={1.75} />
            </button>
          </div>
        </aside>
      </div>

      {/* Right Quick Panel removed — Request / Approvals open from Home circles */}

      {statusModal.open && (
        <RequestStatusDeskModal
          darkMode={darkMode}
          variant="page"
          panelKey={statusModal.panelKey}
          panelTypeItems={panelTypeItemsByKey[statusModal.panelKey] || []}
          sortedPanelTypeItems={sortedTypesByPanel[statusModal.panelKey] || [ALL_DESK_TYPES]}
          groupedTypeModules={groupedTypesByPanel[statusModal.panelKey] || []}
          typeBadgeCounts={typeBadgeCountsByPanel[statusModal.panelKey] || {}}
          statusCounts={deskStatusCounts}
          selectedType={selectedTypeByPanel[statusModal.panelKey] || ALL_DESK_TYPES}
          onSelectType={(typeLabel) => {
            const panelKey = statusModal.panelKey;
            setSelectedTypeByPanel((prev) => ({ ...prev, [panelKey]: typeLabel }));
            setSelectedRequestId(null);
            const deskPath = DESK_ROUTES[panelKey];
            if (deskPath) {
              const typeQ =
                !typeLabel || typeLabel === ALL_DESK_TYPES ? "All" : typeLabel;
              navigate(`${deskPath}?type=${encodeURIComponent(typeQ)}`);
            }
          }}
          selectedStatus={resolveDeskStatusTab(
            selectedStatusByPanel[statusModal.panelKey] || statusModal.status || "Pending"
          )}
          onSelectStatus={(status) => {
            setStatusModal((prev) => ({ ...prev, status }));
            applyStatusSelection(status);
          }}
          onClose={closeStatusModal}
          onOpenForm={openStatusDeskForm}
          filteredRequests={getFilteredRequests()}
          selectedRequestId={selectedRequestId}
          onSelectRequest={setSelectedRequestId}
          onOpenStatusAction={openStatusActionModal}
          onOpenBulkStatusAction={openBulkStatusActionModal}
          onOpenLinkedRecord={openLinkedEmployeeFromDesk}
          onContinueOnboardingDraft={continueOnboardingDraftFromDesk}
        />
      )}
      <QuickRequestFormModal
        darkMode={darkMode}
        isOpen={statusDeskShowForm}
        actionLabel={statusDeskContext?.actionLabel || ""}
        context={statusDeskContext}
        quickActionDraft={quickActionDraft}
        setQuickActionDraft={setQuickActionDraft}
        maxDateToday={maxDateToday}
        onClose={closeStatusDeskForm}
        onSubmitQuickAction={submitQuickAction}
        onSubmitRegularise={submitRegulariseQuickAction}
        onTaxPlanningDeclared={submitTaxPlanningQuickAction}
        isSubmitting={quickActionSubmitting}
      />
      <StructureReimbSyncNotifier />
      <ResignationRequestFormModal
        darkMode={darkMode}
        isOpen={resignationFormOpen}
        employeeId={statusDeskContext?.employeeId || null}
        onClose={() => setResignationFormOpen(false)}
        onSubmitted={() => {
          setResignationFormOpen(false);
          fetchLifecycleDeskRequests();
        }}
      />
      <FlexiReimbursementRequestModal
        isOpen={flexiReimbModal.open}
        mode={flexiReimbModal.mode}
        employeeId={statusDeskContext?.employeeId || null}
        context={statusDeskContext}
        onClose={() => setFlexiReimbModal({ open: false, mode: "declare" })}
        onSubmitted={() => {
          setFlexiReimbModal({ open: false, mode: "declare" });
          fetchFlexiDeskRequests();
          window.dispatchEvent(new CustomEvent("admin:flexi-reimbursement-changed"));
        }}
      />
      {statusActionModal.open && (
        <div className="fixed inset-0 z-[10025] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))]">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3 dark:border-[hsl(var(--ds-border))]">
              <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-fg))]">
                {statusActionModal.actionTitle || statusActionModal.nextStatus}{" "}
                {statusActionModal.requestIds.length > 1
                  ? `${statusActionModal.requestIds.length} Requests`
                  : "Request"}
              </h3>
              <button
                type="button"
                onClick={closeStatusActionModal}
                disabled={statusActionSubmitting}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:text-[hsl(var(--ds-muted-fg))] dark:hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50"
                aria-label="Close reason popup"
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-2 p-4">
              {statusActionModal.nextStatus === "Submitted" ? (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  This sends the onboarding to the Reporting Manager approval
                  queue. Admins can also act from Activity Console afterwards.
                </p>
              ) : (
                <label className="block text-xs font-medium text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
                  Remarks <span className="text-red-600">*</span>
                  <textarea
                    rows={4}
                    maxLength={250}
                    value={statusActionModal.reason}
                    disabled={statusActionSubmitting}
                    onChange={(e) =>
                      setStatusActionModal((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1.5 text-xs text-[hsl(var(--ds-fg))] dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))] dark:text-[hsl(var(--ds-fg))]"
                    placeholder={`Enter remarks for ${(statusActionModal.actionTitle || statusActionModal.nextStatus).toLowerCase()}...`}
                  />
                </label>
              )}
              {statusActionModal.nextStatus !== "Submitted" ? (
                <div className="text-right text-[10px] text-[hsl(var(--ds-muted-fg))]">
                  {(statusActionModal.reason || "").length}/250
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-1">
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={statusActionSubmitting}
                  onClick={closeStatusActionModal}
                >
                  Cancel
                </DsButton>
                <DsButton
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={
                    statusActionSubmitting ||
                    (statusActionModal.nextStatus === "Submitted"
                      ? false
                      : !(statusActionModal.reason || "").trim() ||
                        (statusActionModal.reason || "").trim().length > 250)
                  }
                  onClick={confirmStatusAction}
                >
                  {statusActionSubmitting ? "Confirming…" : "Confirm"}
                </DsButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default Sidebar;
